import * as THREE from 'three';
import { FikoObje3D } from '../../../FikoObje3D';
import { FikoSahne } from '../../../FikoSahne';
import { FikoVektor3D } from '../../../FikoVektor3d';
import { FikoVektorTakimYol3D } from '../../../FikoThreeCam/FikoVektorTakimYol3d';
import { FikoTakimFrezeleme } from '../../../FikoThreeCam/FikoTakimFrezeleme';
import { offsetHat } from '../../../Fiko2dPathSihirbaz';
import { poligonCevresi, tabMerkezleri, tabZHesap } from '../../strategies/cam-toolpath-utils';

/**
 * FikoAktiviteProfil - 2D CAM Profil/Çevre İşleme Aktivitesi
 *
 * Profesyonel CAM sistemleri (Mastercam, Fusion 360, SolidCAM) standartlarında
 * eksiksiz profil işleme motoru.
 *
 * Desteklenen Giriş Yöntemleri:
 * - Dikey (Vertical/Plunge): Doğrudan dikey iniş
 * - Rampa (Ramp): Açılı XY+Z eşli hareket
 * - Helisel (Helical): Spiral iniş
 * - Yay (Arc/Roll-In): Teğet yay girişi (G02/G03)
 * - Teğet (Tangent Line): Düz teğet yaklaşma
 *
 * Desteklenen Çıkış Yöntemleri:
 * - Dikey (Vertical): Doğrudan dikey çıkış
 * - Rampa (Ramp): Açılı yukarı rampa
 * - Yay (Arc/Roll-Out): Teğet yay çıkışı (G02/G03)
 *
 * @author FikoYT
 * @version 2.0.0 - Professional CAM Engine
 */
export class FikoAktiviteProfil extends FikoObje3D {
    static SAYAC = 1;

    override type = 'FikoAktiviteProfil';
    readonly isGroup: true;

    // ═══════════════════════════════════════════════════════════════════
    // TEMEL PARAMETRELER
    // ═══════════════════════════════════════════════════════════════════

    // Profil parametreleri
    profilNoktalar: FikoVektor3D[] = [];

    /** Konturun kapalılığı — yüz seçiminden gelen kontur KESİN kapalı (true), edge zinciri oto-tespit (null).
     *  Köşe-listesi tekrarsız olduğundan salt ilk≈son tespiti kapalı yüz konturunu AÇIK sanıp iki-taraflı bant offset üretiyordu. */
    profilKapali: boolean | null = null;
    profilOfsetliNoktalar: FikoVektor3D[] = []; // Offset uygulanmış profil

    // Düzlem tanımları
    ustZ: number = 0;
    altZ: number = -10;
    guvenliZ: number = 5;
    araGecisZ: number = 2; // Seviyeler arası geçiş yüksekliği

    // Takım
    takim: FikoTakimFrezeleme;
    takimYonu: FikoVektor3D = new FikoVektor3D(0, 0, 1);

    // İşleme parametreleri
    islemeYontemi: 'Tek Yön' | 'Zik Zak' | 'Helisel' = 'Tek Yön';
    yanalYontem: 'İlk Dikey' | 'İlk Yanal' = 'İlk Yanal';
    koseDonusler: 'Keskin' | 'Dairesel' | 'Yakinsal' = 'Keskin'; // sivri köşe (kare/dış kontur doğru); yay isteyen 'Dairesel' seçer

    // Hatve parametreleri
    zHatve: number = 1.0;
    yanalHatve: number = 0.5;
    yanalTekrar: number = 1;

    // ═══════════════════════════════════════════════════════════════════
    // GİRİŞ PARAMETRELERİ (Lead-In)
    // ═══════════════════════════════════════════════════════════════════

    girisYontemi: 'Dikey' | 'Rampa' | 'Helisel' | 'Yay' | 'Teget' = 'Dikey';
    girisUzunluk: number = 5;          // Giriş uzunluğu (mm)
    girisRampaAcisi: number = 15;       // Rampa açısı (1-45 derece)
    girisHeliselCap: number = 10;       // Helisel çap (mm)
    girisHeliselAdim: number = 2;       // Helisel adım/pitch (mm/tur)
    girisYayRadius: number = 5;         // Yay radius (mm)
    girisYayAci: number = 90;           // Yay sweep açısı (10-180 derece)
    girisYonuCW: boolean = true;        // Saat yönü mü (CW/CCW)
    girisTegetUzunluk: number = 5;      // Teğet yaklaşma uzunluğu

    // ═══════════════════════════════════════════════════════════════════
    // ÇIKIŞ PARAMETRELERİ (Lead-Out)
    // ═══════════════════════════════════════════════════════════════════

    cikisYontemi: 'Dikey' | 'Rampa' | 'Yay' = 'Dikey';
    cikisUzunluk: number = 5;           // Çıkış uzunluğu (mm)
    cikisRampaAcisi: number = 15;       // Rampa açısı (derece)
    cikisYayRadius: number = 5;         // Yay radius (mm)
    cikisYayAci: number = 90;           // Yay sweep açısı (derece)
    cikisYonuCW: boolean = false;       // Saat yönü mü

    // ═══════════════════════════════════════════════════════════════════
    // HIZ PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    ilerlemeYaklasma: number = 3000;    // Yaklaşma hızı (mm/dk)
    ilerlemeIsleme: number = 1500;      // İşleme hızı (mm/dk)
    ilerlemeUzaklasma: number = 5000;   // Uzaklaşma hızı (mm/dk)
    ilerlemeDalma: number = 500;        // Dalma hızı (mm/dk)
    ilerlemeCekme: number = 3000;       // Çekme hızı (mm/dk)
    ilerlemeLead: number = 100;         // Lead hız yüzdesi (%)
    hizRapid: number = 10000;           // Rapid hız (mm/dk)
    devir: number = 3000;               // Spindle devri (rpm)

    // ═══════════════════════════════════════════════════════════════════
    // OFSET PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    ofsetYonu: 'Sol' | 'Sag' | 'Merkez' = 'Sol';
    ofsetMiktari: number = 0;           // Ek ofset (finish için)
    yuzeyPayi: number = 0.5;            // Yüzey payı (mm)

    // ═══════════════════════════════════════════════════════════════════
    // UZATMA & OVERLAP
    // ═══════════════════════════════════════════════════════════════════

    basUzatma: number = 0;              // Profil başına uzatma (mm)
    sonUzatma: number = 0;              // Profil sonuna uzatma (mm)
    overlap: number = 0;                // Overlap yüzdesi (%)
    overcutEtkin: boolean = false;      // Overcut aktif mi

    // ═══════════════════════════════════════════════════════════════════
    // SON TUR (FİNİŞ) PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    sonTurEtkin: boolean = true;
    sonTurPayi: number = 0.2;           // Finiş payı (mm)
    sonTurIlerleme: number = 800;       // Finiş ilerleme (mm/dk)
    sonTurDevir: number = 4000;         // Finiş devri (rpm)
    sonTurAyriGiris: boolean = false;   // Ayrı giriş kullan

    // ═══════════════════════════════════════════════════════════════════
    // GEÇİŞ PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    gecisTipi: 'Direkt' | 'Yumusak' | 'Profil' = 'Direkt';

    // ═══════════════════════════════════════════════════════════════════
    // TABS (KÖPRÜ/TUTMA) PARAMETRELERİ - Parça Sabitleme
    // ═══════════════════════════════════════════════════════════════════

    tabEtkin: boolean = false;              // Tab/köprü aktif mi
    tabSekli: 'Dikdortgen' | 'Ucgen' = 'Dikdortgen';  // Tab şekli
    tabGenislik: number = 5;                // Tab genişliği (mm)
    tabYukseklik: number = 2;               // Tab yüksekliği (mm)
    tabSayisi: number = 4;                  // Profil başına tab sayısı
    tabMesafe: number = 50;                 // Tablar arası mesafe (mm)
    tabDagitim: 'Sayi' | 'Mesafe' = 'Sayi'; // Dağıtım yöntemi

    // ═══════════════════════════════════════════════════════════════════
    // KÖŞE YAVAŞLAMA (Corner Slowdown) - Profesyonel Özellik
    // ═══════════════════════════════════════════════════════════════════

    koseYavaslamaEtkin: boolean = false;    // Köşe yavaşlama aktif mi
    koseYavaslamaOrani: number = 50;        // Yavaşlama oranı (%)
    koseMinAci: number = 90;                // Minimum tetikleme açısı (derece)
    koseYavaslamaTipi: 'Tum' | 'Ic' | 'Dis' = 'Tum';  // Hangi köşelerde

    // ═══════════════════════════════════════════════════════════════════
    // KOMPANZASYON PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    kompanzasyonTipi: 'Bilgisayar' | 'Kontrol' | 'Kapali' = 'Bilgisayar';
    kompanzasyonTolereans: number = 0.05;   // Kompanzasyon toleransı (mm)

    // ═══════════════════════════════════════════════════════════════════
    // STAY-DOWN / LIFT (Kaldırma Kontrolü) - Gereksiz Retrakt Önleme
    // ═══════════════════════════════════════════════════════════════════

    stayDownEtkin: boolean = true;          // Stay-down aktif mi
    stayDownMesafe: number = 10;            // Max stay-down mesafesi (mm)
    kaldirmaMesafesi: number = 2;           // Lift mesafesi (mm)

    // ═══════════════════════════════════════════════════════════════════
    // YOL YUMUŞATMA (Path Smoothing)
    // ═══════════════════════════════════════════════════════════════════

    yumusatmaEtkin: boolean = false;        // Yol yumuşatma aktif mi
    yumusatmaRadius: number = 0.5;          // Yumuşatma radius (mm)
    yumusatmaTolerans: number = 0.01;       // Yumuşatma toleransı (mm)

    // ═══════════════════════════════════════════════════════════════════
    // FREZE YÖNÜ (Climb vs Conventional)
    // ═══════════════════════════════════════════════════════════════════

    frezeYonu: 'Tirmanma' | 'Konvansiyonel' = 'Tirmanma';  // Climb = Tırmanma

    // ═══════════════════════════════════════════════════════════════════
    // ÇOKLU FİNİŞ GEÇİŞİ
    // ═══════════════════════════════════════════════════════════════════

    finishGecisSayisi: number = 1;          // Finiş geçiş sayısı
    finishStockAzaltma: number = 0.1;       // Her geçişte azaltılacak pay (mm)

    // ═══════════════════════════════════════════════════════════════════
    // GÜVENLİK PARAMETRELERİ
    // ═══════════════════════════════════════════════════════════════════

    minGuvenliMesafe: number = 1;           // Min güvenli mesafe (mm)
    carpismaDenetimi: boolean = true;       // Çarpışma denetimi aktif mi

    // ═══════════════════════════════════════════════════════════════════
    // HESAPLANAN VERİLER
    // ═══════════════════════════════════════════════════════════════════

    takimYoluNoktalar: FikoVektorTakimYol3D[] = [];
    takimYoluLine: THREE.Line;
    daireselSapma: number = 0.01;       // Dairesel interpolasyon sapması

    // G-code çıktı buffer
    private gcodeBuffer: string[] = [];

    public get liste(): any[] {
        return this.children;
    }

    sagButonlar?: any[] = [
        {
            isim: "Similasyon Yap",
            simge: "play",
            tiklandi: () => {
                // Similasyon başlat
            }
        },
        {
            isim: "Hesapla",
            simge: "calculator",
            tiklandi: () => {
                this.hesapla();
            }
        }
    ];

    sagMenu: any[] = [
        {
            simge: "edit",
            baslik: "Düzenle",
            tiklandi: () => {
                this.duzenle();
            }
        },
        {
            simge: null,
            baslik: "Gizle / Göster",
            tiklandi: () => {
                this.visible = !this.visible;
                this.sahne.anaGosterim.FikoYT.degisti.emit();
                this.sahne.anaGosterim.ciz();
            }
        },
        {
            tipi: "Ayraç"
        },
        {
            simge: "trash alternate",
            baslik: "Sil",
            renkSinif: "red",
            tiklandi: () => {
                this.sil();
            }
        }
    ];

    aracCubugu: any[] = [
        {
            simge: "edit",
            baslik: "Düzenle",
            tiklandi: () => {
                this.duzenle();
            }
        },
        {
            simge: "calculator",
            baslik: "Hesapla",
            tiklandi: () => {
                this.hesapla();
            }
        },
        {
            simge: "trash alternate",
            baslik: "Sil",
            renkSinif: "red",
            tiklandi: () => {
                this.sil();
            }
        }
    ];

    constructor(public override sahne?: FikoSahne, isim?: string) {
        super(sahne);

        // Varsayılan takım
        this.takim = new FikoTakimFrezeleme(this.sahne, 10);

        // Arayüz ayarları
        this.userData.FikoArayuz.agactaGorunur = true;
        this.userData.FikoArayuz.listeGoster = true;
        this.userData.FikoArayuz.simge = "/simgeler/cam-profil.svg";

        if (!isim) {
            this.name = "Profil." + FikoAktiviteProfil.SAYAC;
            FikoAktiviteProfil.SAYAC++;
        } else {
            this.name = isim;
        }
    }

    /**
     * Profil noktalarını ayarla
     */
    setProfilNoktalar(noktalar: FikoVektor3D[]): void {
        // Geçerli noktaları filtrele (NaN ve undefined değerleri çıkar)
        this.profilNoktalar = (noktalar || []).filter(n =>
            n != null &&
            typeof n.x === 'number' && !isNaN(n.x) && isFinite(n.x) &&
            typeof n.y === 'number' && !isNaN(n.y) && isFinite(n.y) &&
            typeof n.z === 'number' && !isNaN(n.z) && isFinite(n.z)
        );

        if (this.profilNoktalar.length !== (noktalar?.length || 0)) {
            console.warn(`[FikoCAM] ${(noktalar?.length || 0) - this.profilNoktalar.length} geçersiz nokta filtrelendi`);
        }

        // Offset'i sıfırla, hesapla() çağrıldığında yeniden hesaplanacak
        this.profilOfsetliNoktalar = [];
    }

    // ═══════════════════════════════════════════════════════════════════
    // ANA HESAPLAMA MOTORU
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Takım yolunu hesapla - Profesyonel CAM Motoru
     *
     * HESAPLAMA AKIŞI:
     * 1. Yanal offset profilleri oluştur (yanalHatve ile)
     * 2. Z seviyeleri hesapla (zHatve ile)
     * 3. Her seviye için: Giriş -> Profil turları -> Çıkış
     * 4. İşleme yöntemine göre: Tek Yön / Zik Zak / Helisel
     */
    hesapla(): void {
        this.takimYoluNoktalar = [];
        this.gcodeBuffer = [];

        if (this.profilNoktalar.length < 2) {
            console.warn('[FikoCAM] Profil noktaları yetersiz');
            return;
        }


        // 1. Tüm yanal offset profillerini hesapla
        const yanalProfiller = this.hesaplaYanalProfiller();

        // 2. Z seviyelerini hesapla
        const zSeviyeleri = this.hesaplaZSeviyeleri();

        // 3. İşleme yöntemine göre takım yolu hesapla
        switch (this.islemeYontemi) {
            case 'Zik Zak':
                this.hesaplaZikZak(yanalProfiller, zSeviyeleri);
                break;
            case 'Helisel':
                this.hesaplaHeliselIsleme(yanalProfiller, zSeviyeleri);
                break;
            case 'Tek Yön':
            default:
                this.hesaplaTekYon(yanalProfiller, zSeviyeleri);
                break;
        }

        // 4. Son tur (finish) - ayrı payı ile
        if (this.sonTurEtkin && this.sonTurPayi > 0) {
            this.sonTurHesapla();
        }

        // 5. Son çıkış hareketi
        this.cikisHesapla();

        // 6. Takım yolu görselini güncelle
        this.guncelleTakimYoluGorsel();

    }

    // ═══════════════════════════════════════════════════════════════════
    // YANAL HATVE PROFİLLERİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Yanal offset profillerini hesapla
     * Her yanal geçiş için ayrı offset profil oluşturur
     *
     * İşleme Mantığı:
     * - "İlk Yanal" modu: Dıştan içe doğru işleme (önce en büyük offset, sonra küçülen)
     * - "İlk Dikey" modu: İçten dışa doğru işleme (önce en küçük offset, sonra büyüyen)
     *
     * Offset Yönü:
     * - "Sol": Negatif offset (profil dışına)
     * - "Sag": Pozitif offset (profil içine)
     * - "Merkez": Sıfır offset
     */
    private hesaplaYanalProfiller(): FikoVektor3D[][] {
        const profiller: FikoVektor3D[][] = [];

        // Temel offset (takım yarıçapı + kullanıcı offset)
        const temelOfset = this.hesaplaOfsetMiktar();

        // Yanal tekrar sayısına göre profiller oluştur
        const tekrarSayisi = Math.max(1, this.yanalTekrar);

        // Offset yönü faktörü: Sol = dışa (-), Sag = içe (+)
        const yonFaktor = this.ofsetYonu === 'Sag' ? 1 : -1;

        // "İlk Yanal" modunda dıştan içe: en büyük offsetten başla
        // "İlk Dikey" modunda içten dışa: en küçük offsetten başla
        const disIc = this.yanalYontem === 'İlk Yanal';

        for (let i = 0; i < tekrarSayisi; i++) {
            // Yanal geçiş indexi (dıştan içe veya içten dışa)
            const gecisIdx = disIc ? (tekrarSayisi - 1 - i) : i;

            // Her geçiş için ek offset hesapla
            const ekOfset = gecisIdx * this.yanalHatve;
            const toplamOfset = temelOfset + (ekOfset * yonFaktor);

            // Bu offset için profil hesapla
            const profilNoktalar = this.hesaplaOfsetProfilIle(toplamOfset);

            if (profilNoktalar.length >= 2) {
                profiller.push(profilNoktalar);
            }
        }

        // En az bir profil olsun
        if (profiller.length === 0) {
            profiller.push([...this.profilNoktalar]);
        }

        return profiller;
    }

    /**
     * Belirli bir offset değeri ile profil hesapla
     */
    /** Son offset hesabı başarısız oldu mu (takım merkezi kontur ÜZERİNDE kalır — parça ölçüsü takım yarıçapı kadar hatalı olur) */
    public ofsetHesapHatasi: string | null = null;

    private hesaplaOfsetProfilIle(ofset: number): FikoVektor3D[] {
        if (Math.abs(ofset) < 0.001) {
            return [...this.profilNoktalar];
        }

        try {
            const offsetResult = offsetHat(
                this.profilNoktalar,
                ofset,
                this.koseDonusler as any,
                this.daireselSapma
            );

            if (offsetResult && offsetResult.length > 0) {
                const gecerli = offsetResult
                    .filter((p: any) =>
                        p != null &&
                        typeof p.x === 'number' && !isNaN(p.x) && isFinite(p.x) &&
                        typeof p.y === 'number' && !isNaN(p.y) && isFinite(p.y)
                    )
                    .map((p: any) => new FikoVektor3D(p.x, p.y, 0));
                if (gecerli.length >= 2) {
                    this.ofsetHesapHatasi = null;
                    return gecerli;
                }
            }
        } catch (e) {
            console.warn('[FikoCAM] Offset hatası:', e);
        }

        this.ofsetHesapHatasi = `Takım yarıçapı telafisi (${ofset.toFixed(2)}mm) hesaplanamadı — takım merkezi kontur üzerinde, parça ölçüsü hatalı olur!`;
        console.warn('[FikoCAM] ' + this.ofsetHesapHatasi);
        return [...this.profilNoktalar];
    }

    // ═══════════════════════════════════════════════════════════════════
    // TEK YÖN İŞLEME
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Tek yön işleme - Her seviyede aynı yönde geçiş
     * Düzeltilmiş versiyon: Offset'li profilleri doğru şekilde kullanır
     */
    private hesaplaTekYon(yanalProfiller: FikoVektor3D[][], zSeviyeleri: number[]): void {
        if (yanalProfiller.length === 0 || zSeviyeleri.length === 0) return;

        // İlk offset'li profilin ilk noktasına rapid
        this.rapidGuvenliZ(yanalProfiller[0]);

        for (let zIdx = 0; zIdx < zSeviyeleri.length; zIdx++) {
            const hedefZ = zSeviyeleri[zIdx];
            const baslangicZ = zIdx === 0 ? this.guvenliZ : zSeviyeleri[zIdx - 1];

            // Her yanal profil için
            for (let profilIdx = 0; profilIdx < yanalProfiller.length; profilIdx++) {
                const profil = yanalProfiller[profilIdx];

                if (profil.length < 2) continue;

                // İlk profil ilk seviyede giriş hareketi yap
                if (profilIdx === 0 && zIdx === 0) {
                    // İlk giriş - offset'li profili kullan
                    this.girisHesapla(hedefZ, baslangicZ, profil);
                } else if (profilIdx === 0) {
                    // Yeni Z seviyesine sadece dalma (takım zaten malzeme içinde)
                    this.zSeviyesiDalma(hedefZ, profil);
                } else {
                    // Yanal geçiş - önceki profilden bu profile
                    this.yanalGecisHesapla(yanalProfiller[profilIdx - 1], profil, hedefZ);
                }

                // Profil turu
                this.profilTuruHesaplaIle(profil, hedefZ, false);

                // Overlap
                if (this.overlap > 0) {
                    this.overlapHesaplaIle(profil, hedefZ);
                }
            }

            // Ara çıkış (son seviye değilse)
            if (zIdx < zSeviyeleri.length - 1) {
                this.araCikisHesapla(hedefZ);
            }
        }
    }

    /**
     * Z seviyesine dalma - Takım zaten malzeme içinde, sadece Z değişir
     * Tam giriş hareketi yerine basit dalma yapar
     */
    private zSeviyesiDalma(hedefZ: number, profil: FikoVektor3D[]): void {
        if (profil.length === 0) return;

        const ilkNokta = profil[0];

        // Önce profilin ilk noktasının XY konumuna git (stay-down veya hafif kaldırma ile)
        const sonNokta = this.takimYoluNoktalar.length > 0
            ? this.takimYoluNoktalar[this.takimYoluNoktalar.length - 1]
            : null;

        if (sonNokta) {
            const mesafe = Math.sqrt(
                Math.pow(ilkNokta.x - sonNokta.x, 2) +
                Math.pow(ilkNokta.y - sonNokta.y, 2)
            );

            // Eğer XY konumu farklıysa, önce XY'ye git
            if (mesafe > 0.01) {
                const xyGecis = new FikoVektorTakimYol3D(ilkNokta.x, ilkNokta.y, sonNokta.z);
                xyGecis.tip = 'linear';
                xyGecis.hiz = this.ilerlemeIsleme;
                xyGecis.devir = this.devir;
                xyGecis.renk = new THREE.Color(0x00aaff); // Mavi - XY geçiş
                this.takimYoluNoktalar.push(xyGecis);
            }
        }

        // Yeni Z seviyesine dalma
        const dalma = new FikoVektorTakimYol3D(ilkNokta.x, ilkNokta.y, hedefZ);
        dalma.tip = 'linear';
        dalma.hiz = this.ilerlemeDalma;
        dalma.devir = this.devir;
        dalma.renk = new THREE.Color(0xffff00); // Sarı - dalma
        this.takimYoluNoktalar.push(dalma);
    }

    // ═══════════════════════════════════════════════════════════════════
    // ZİK ZAK İŞLEME
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Zik Zak işleme - Her seviyede yön değiştirir
     * Düzeltilmiş versiyon: Offset'li profilleri doğru şekilde kullanır
     */
    private hesaplaZikZak(yanalProfiller: FikoVektor3D[][], zSeviyeleri: number[]): void {
        if (yanalProfiller.length === 0 || zSeviyeleri.length === 0) return;

        // İlk offset'li profilin ilk noktasına rapid
        this.rapidGuvenliZ(yanalProfiller[0]);

        let ters = false; // Yön kontrolü

        for (let zIdx = 0; zIdx < zSeviyeleri.length; zIdx++) {
            const hedefZ = zSeviyeleri[zIdx];
            const baslangicZ = zIdx === 0 ? this.guvenliZ : zSeviyeleri[zIdx - 1];

            // Profilleri yöne göre sırala
            const siraliProfiller = ters ? [...yanalProfiller].reverse() : yanalProfiller;

            for (let profilIdx = 0; profilIdx < siraliProfiller.length; profilIdx++) {
                const profil = siraliProfiller[profilIdx];

                if (profil.length < 2) continue;

                // Profil noktalarını da ters çevir (Zik Zak efekti)
                const islenenProfil = ters ? [...profil].reverse() : profil;

                if (profilIdx === 0 && zIdx === 0) {
                    // İlk giriş - offset'li profili kullan
                    this.girisHesapla(hedefZ, baslangicZ, islenenProfil);
                } else if (profilIdx === 0) {
                    // Yeni Z seviyesine sadece dalma
                    this.zSeviyesiDalma(hedefZ, islenenProfil);
                } else {
                    const oncekiProfil = siraliProfiller[profilIdx - 1];
                    const tersOnceki = ters ? [...oncekiProfil].reverse() : oncekiProfil;
                    this.yanalGecisHesapla(tersOnceki, islenenProfil, hedefZ);
                }

                this.profilTuruHesaplaIle(islenenProfil, hedefZ, false);

                if (this.overlap > 0) {
                    this.overlapHesaplaIle(islenenProfil, hedefZ);
                }
            }

            // Yön değiştir
            ters = !ters;

            if (zIdx < zSeviyeleri.length - 1) {
                this.araCikisHesapla(hedefZ);
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // HELİSEL İŞLEME
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Helisel işleme - Spiral şeklinde Z'ye inerken profil izler
     * Düzeltilmiş versiyon: Offset'li profilleri doğru şekilde kullanır
     */
    private hesaplaHeliselIsleme(yanalProfiller: FikoVektor3D[][], zSeviyeleri: number[]): void {
        if (yanalProfiller.length === 0 || yanalProfiller[0].length < 2) return;

        // İlk offset'li profilin ilk noktasına rapid
        this.rapidGuvenliZ(yanalProfiller[0]);

        // Her yanal profil için helisel iniş
        for (let profilIdx = 0; profilIdx < yanalProfiller.length; profilIdx++) {
            const profil = yanalProfiller[profilIdx];

            if (profil.length < 2) continue;

            const ilkNokta = profil[0];

            // İlk profile giriş
            if (profilIdx === 0) {
                // Güvenli Z'den ilk noktaya (offset'li profil zaten rapidGuvenliZ ile ayarlandı)
                // Üst Z'ye dalma
                const dalma = new FikoVektorTakimYol3D(ilkNokta.x, ilkNokta.y, this.ustZ);
                dalma.tip = 'linear';
                dalma.hiz = this.ilerlemeDalma;
                dalma.devir = this.devir;
                dalma.renk = new THREE.Color(0xffff00);
                this.takimYoluNoktalar.push(dalma);
            } else {
                // Önceki profilden bu profile yanal geçiş
                this.yanalGecisHesapla(yanalProfiller[profilIdx - 1], profil, this.ustZ);
            }

            // Helisel iniş - Z'yi kademeli düşürerek profil izle
            const toplamZFark = this.ustZ - this.altZ;
            const turBasinaZ = this.zHatve; // Her profil turunda düşecek Z
            const turSayisi = Math.ceil(toplamZFark / turBasinaZ);

            let mevcutZ = this.ustZ;

            for (let tur = 0; tur < turSayisi; tur++) {
                const turHedefZ = Math.max(this.altZ, mevcutZ - turBasinaZ);
                const zAdim = (mevcutZ - turHedefZ) / profil.length;

                // Profil boyunca ilerlerken Z'yi düşür
                for (let i = 0; i < profil.length; i++) {
                    const nokta = profil[i];
                    const z = mevcutZ - (i * zAdim);

                    const takimNokta = new FikoVektorTakimYol3D(nokta.x, nokta.y, z);
                    takimNokta.tip = 'linear';
                    takimNokta.hiz = this.ilerlemeIsleme;
                    takimNokta.devir = this.devir;
                    takimNokta.renk = new THREE.Color(0xff00ff); // Magenta - helisel
                    this.takimYoluNoktalar.push(takimNokta);
                }

                mevcutZ = turHedefZ;

                if (mevcutZ <= this.altZ) break;
            }

            // Profil kapalı ise son bir tam tur at (alt Z'de)
            if (this.profilKapaliMi()) {
                // Tab/köprü: sadece son (parça sınırı) geçişte parça tutma köprüleri bırak
                if (this.tabEtkin && profilIdx === yanalProfiller.length - 1) {
                    this.altZTuruTablarlaCiz(profil);
                } else {
                    for (const nokta of profil) {
                        const takimNokta = new FikoVektorTakimYol3D(nokta.x, nokta.y, this.altZ);
                        takimNokta.tip = 'linear';
                        takimNokta.hiz = this.ilerlemeIsleme;
                        takimNokta.devir = this.devir;
                        takimNokta.renk = new THREE.Color(0x00ffff);
                        this.takimYoluNoktalar.push(takimNokta);
                    }
                }
            }
        }
    }

    /**
     * Alt Z kapanış turunu tab/köprülerle çizer.
     * Çevre boyunca tab konumlarında takımı (altZ + tabYukseklik)'e kaldırır →
     * parça through-cut sonrası yerinde kalır (sac/levha güvenliği).
     */
    private altZTuruTablarlaCiz(profil: FikoVektor3D[]): void {
        const n = profil.length;
        if (n < 2) return;

        const { cevre, segLen } = poligonCevresi(profil);
        if (cevre < 1e-6) return;

        const merkezler = tabMerkezleri(cevre, {
            tabSayisi: this.tabSayisi, tabDagitim: this.tabDagitim, tabMesafe: this.tabMesafe,
            tabGenislik: this.tabGenislik, tabYukseklik: this.tabYukseklik, tabSekli: this.tabSekli
        });
        if (merkezler.length === 0) {
            for (const nokta of profil) this.altZNoktaEkle(nokta.x, nokta.y, this.altZ, 0x00ffff);
            return;
        }

        const yariGen = Math.max(0.1, this.tabGenislik / 2);
        const tabZ = (s: number): number =>
            tabZHesap(s, merkezler, cevre, yariGen, this.altZ, this.tabYukseklik, this.tabSekli);

        const adim = Math.max(0.5, yariGen / 2); // örnekleme adımı (tab geçişlerini yakala)
        this.altZNoktaEkle(profil[0].x, profil[0].y, tabZ(0), 0x00ffff);

        let s = 0;
        for (let i = 0; i < n; i++) {
            const a = profil[i], b = profil[(i + 1) % n];
            const L = segLen[i];
            if (L < 1e-9) continue;
            const dx = (b.x - a.x) / L, dy = (b.y - a.y) / L;
            let yerel = adim;
            while (yerel < L) {
                const ss = s + yerel;
                const z = tabZ(ss);
                this.altZNoktaEkle(a.x + dx * yerel, a.y + dy * yerel, z, z > this.altZ + 1e-6 ? 0xffff00 : 0x00ffff);
                yerel += adim;
            }
            s += L;
            const z = tabZ(s % cevre);
            this.altZNoktaEkle(b.x, b.y, z, z > this.altZ + 1e-6 ? 0xffff00 : 0x00ffff);
        }
    }

    private altZNoktaEkle(x: number, y: number, z: number, renk: number): void {
        const p = new FikoVektorTakimYol3D(x, y, z);
        p.tip = 'linear';
        p.hiz = this.ilerlemeIsleme;
        p.devir = this.devir;
        p.renk = new THREE.Color(renk);
        this.takimYoluNoktalar.push(p);
    }

    // ═══════════════════════════════════════════════════════════════════
    // YANAL GEÇİŞ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Yanal geçiş - Bir profilden diğerine (GÜVENLİ)
     *
     * KRİTİK DÜZELTME: Takım malzeme içinden geçmemeli!
     * Önceki profilden yeni profile geçerken:
     * 1. Stay-down aktif ve mesafe kısaysa: Hafif kaldır, git, indir
     * 2. Mesafe uzunsa: Güvenli Z'ye çık, git, indir
     */
    private yanalGecisHesapla(oncekiProfil: FikoVektor3D[], yeniProfil: FikoVektor3D[], z: number): void {
        if (yeniProfil.length === 0) return;

        const hedefNokta = yeniProfil[0];

        // Son eklenen noktayı al (önceki profilin son noktası)
        const sonNokta = this.takimYoluNoktalar.length > 0
            ? this.takimYoluNoktalar[this.takimYoluNoktalar.length - 1]
            : null;

        if (!sonNokta) {
            // İlk geçiş - direkt hedefe git
            const gecis = new FikoVektorTakimYol3D(hedefNokta.x, hedefNokta.y, z);
            gecis.tip = 'linear';
            gecis.hiz = this.ilerlemeIsleme;
            gecis.devir = this.devir;
            gecis.renk = new THREE.Color(0xffaa00);
            this.takimYoluNoktalar.push(gecis);
            return;
        }

        // Mesafeyi hesapla
        const mesafe = Math.sqrt(
            Math.pow(hedefNokta.x - sonNokta.x, 2) +
            Math.pow(hedefNokta.y - sonNokta.y, 2)
        );

        // Stay-down kullan (kısa mesafe)
        if (this.stayDownEtkin && mesafe <= this.stayDownMesafe) {
            // 1. Hafif kaldır (lift)
            const liftZ = z + this.kaldirmaMesafesi;
            const lift = new FikoVektorTakimYol3D(sonNokta.x, sonNokta.y, liftZ);
            lift.tip = 'linear';
            lift.hiz = this.ilerlemeCekme;
            lift.devir = this.devir;
            lift.renk = new THREE.Color(0xffff00); // Sarı - kaldırma
            this.takimYoluNoktalar.push(lift);

            // 2. Hedefe XY'de git
            const xyGecis = new FikoVektorTakimYol3D(hedefNokta.x, hedefNokta.y, liftZ);
            xyGecis.tip = 'linear';
            xyGecis.hiz = this.ilerlemeYaklasma;
            xyGecis.devir = this.devir;
            xyGecis.renk = new THREE.Color(0xffaa00); // Turuncu - XY geçiş
            this.takimYoluNoktalar.push(xyGecis);

            // 3. Z'ye indir
            const inis = new FikoVektorTakimYol3D(hedefNokta.x, hedefNokta.y, z);
            inis.tip = 'linear';
            inis.hiz = this.ilerlemeDalma;
            inis.devir = this.devir;
            inis.renk = new THREE.Color(0xffff00); // Sarı - dalma
            this.takimYoluNoktalar.push(inis);
        } else {
            // Full retract (uzun mesafe veya stay-down kapalı)
            // 1. Güvenli Z'ye çık
            const retract = new FikoVektorTakimYol3D(sonNokta.x, sonNokta.y, this.guvenliZ);
            retract.tip = 'rapid';
            retract.hiz = this.hizRapid;
            retract.devir = this.devir;
            retract.renk = new THREE.Color(0x00ff00); // Yeşil - rapid
            this.takimYoluNoktalar.push(retract);

            // 2. Hedefe rapid
            const rapid = new FikoVektorTakimYol3D(hedefNokta.x, hedefNokta.y, this.guvenliZ);
            rapid.tip = 'rapid';
            rapid.hiz = this.hizRapid;
            rapid.devir = this.devir;
            rapid.renk = new THREE.Color(0x00ff00);
            this.takimYoluNoktalar.push(rapid);

            // 3. Z'ye dalma
            const dalma = new FikoVektorTakimYol3D(hedefNokta.x, hedefNokta.y, z);
            dalma.tip = 'linear';
            dalma.hiz = this.ilerlemeDalma;
            dalma.devir = this.devir;
            dalma.renk = new THREE.Color(0xffff00);
            this.takimYoluNoktalar.push(dalma);
        }
    }

    /**
     * Profil turu hesapla - Parametre ile
     */
    private profilTuruHesaplaIle(profil: FikoVektor3D[], z: number, ters: boolean): void {
        const islenenProfil = ters ? [...profil].reverse() : profil;

        for (const nokta of islenenProfil) {
            const takimNokta = new FikoVektorTakimYol3D(nokta.x, nokta.y, z);
            takimNokta.tip = 'linear';
            takimNokta.hiz = this.ilerlemeIsleme;
            takimNokta.devir = this.devir;
            takimNokta.renk = new THREE.Color(0x00ffff); // Cyan - işleme
            this.takimYoluNoktalar.push(takimNokta);
        }

        // Kapalı profil ise ilk noktaya dön
        if (this.profilKapaliMi() && islenenProfil.length > 0) {
            const ilkNokta = islenenProfil[0];
            const kapanisNokta = new FikoVektorTakimYol3D(ilkNokta.x, ilkNokta.y, z);
            kapanisNokta.tip = 'linear';
            kapanisNokta.hiz = this.ilerlemeIsleme;
            kapanisNokta.devir = this.devir;
            kapanisNokta.renk = new THREE.Color(0x00ffff);
            this.takimYoluNoktalar.push(kapanisNokta);
        }
    }

    /**
     * Overlap hesapla - Parametre ile
     */
    private overlapHesaplaIle(profil: FikoVektor3D[], z: number): void {
        if (profil.length < 2 || this.overlap <= 0) return;

        const toplamNokta = profil.length;
        const overlapNokta = Math.ceil(toplamNokta * (this.overlap / 100));

        for (let i = 1; i <= overlapNokta && i < profil.length; i++) {
            const nokta = profil[i];
            const takimNokta = new FikoVektorTakimYol3D(nokta.x, nokta.y, z);
            takimNokta.tip = 'linear';
            takimNokta.hiz = this.ilerlemeIsleme;
            takimNokta.devir = this.devir;
            takimNokta.renk = new THREE.Color(0x00aaff); // Mavi - overlap
            this.takimYoluNoktalar.push(takimNokta);
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // OFFSET HESAPLAMA (Clipper Entegrasyonu)
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Offset'li profil hesapla - Clipper kütüphanesi ile
     */
    private hesaplaOfsetProfil(): void {
        const ofset = this.hesaplaOfsetMiktar();

        if (Math.abs(ofset) < 0.001) {
            // Offset yok, orijinal profili kullan
            this.profilOfsetliNoktalar = [...this.profilNoktalar];
            return;
        }

        try {
            // Clipper offsetHat fonksiyonu ile offset uygula
            const offsetResult = offsetHat(
                this.profilNoktalar,
                ofset,
                this.koseDonusler as any,
                this.daireselSapma
            );

            if (offsetResult && offsetResult.length > 0) {
                // Geçerli noktaları filtrele
                this.profilOfsetliNoktalar = offsetResult
                    .filter((p: any) =>
                        p != null &&
                        typeof p.x === 'number' && !isNaN(p.x) && isFinite(p.x) &&
                        typeof p.y === 'number' && !isNaN(p.y) && isFinite(p.y)
                    )
                    .map((p: any) => new FikoVektor3D(p.x, p.y, 0));

                if (this.profilOfsetliNoktalar.length < 2) {
                    console.warn('[FikoCAM] Offset sonucu yetersiz geçerli nokta, orijinal profil kullanılıyor');
                    this.profilOfsetliNoktalar = [...this.profilNoktalar];
                } else {
                }
            } else {
                console.warn('[FikoCAM] Offset hesaplanamadı, orijinal profil kullanılıyor');
                this.profilOfsetliNoktalar = [...this.profilNoktalar];
            }
        } catch (e) {
            console.warn('[FikoCAM] Clipper offset hatası:', e);
            this.profilOfsetliNoktalar = [...this.profilNoktalar];
        }
    }

    /**
     * Ofset miktarını hesapla (takım yarıçapı + ek offset)
     */
    private hesaplaOfsetMiktar(): number {
        let ofset = this.takim.cap / 2;

        switch (this.ofsetYonu) {
            case 'Sol':
                ofset = -ofset - this.ofsetMiktari;
                break;
            case 'Sag':
                ofset = ofset + this.ofsetMiktari;
                break;
            case 'Merkez':
                ofset = this.ofsetMiktari;
                break;
        }

        return ofset;
    }

    /**
     * Finish (son tur) için ofset hesapla
     * Roughing offset - yüzey payı = finish offset
     */
    private hesaplaFinishOfset(): number {
        let ofset = this.takim.cap / 2;

        switch (this.ofsetYonu) {
            case 'Sol':
                ofset = -ofset;
                break;
            case 'Sag':
                ofset = ofset;
                break;
            case 'Merkez':
                ofset = 0;
                break;
        }

        return ofset;
    }

    /**
     * Z seviyelerini hesapla
     * Düzeltilmiş versiyon: İlk Z seviyesi (ustZ) dahil edilir
     *
     * Örnek: ustZ=0, altZ=-10, zHatve=2, sonTurPayi=0.2
     * Çıktı: [0, -2, -4, -6, -8, -9.8]
     *
     * Son tur aktifse, son seviye altZ + sonTurPayi olur (finish için pay bırakılır)
     */
    private hesaplaZSeviyeleri(): number[] {
        const seviyeler: number[] = [];

        // Hedef Z: Son tur aktifse pay bırak
        const hedefZ = this.altZ + (this.sonTurEtkin ? this.sonTurPayi : 0);

        // İlk seviye: üst Z (malzeme üst yüzeyi)
        let z = this.ustZ;

        // İlk Z seviyesini ekle (üst yüzey işleme)
        seviyeler.push(z);

        // Kalan seviyeleri hesapla
        while (z > hedefZ) {
            z -= this.zHatve;

            // Hedef Z'nin altına inme
            if (z < hedefZ) {
                z = hedefZ;
            }

            // Aynı seviyeyi tekrar ekleme
            if (seviyeler.length === 0 || Math.abs(z - seviyeler[seviyeler.length - 1]) > 0.001) {
                seviyeler.push(z);
            }
        }

        // En az bir seviye olsun
        if (seviyeler.length === 0) {
            seviyeler.push(hedefZ);
        }


        return seviyeler;
    }

    // ═══════════════════════════════════════════════════════════════════
    // GİRİŞ HAREKETİ HESAPLAMA (5 YÖNTEM)
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Güvenli Z'ye rapid hareket
     * Offset'li profilin ilk noktasını kullanır (varsa)
     * @param hedefProfil - Hedef profil noktaları (offset'li)
     */
    private rapidGuvenliZ(hedefProfil?: FikoVektor3D[]): void {
        // Offset'li profil verilmişse onu kullan, yoksa orijinal profili
        const profil = hedefProfil && hedefProfil.length > 0
            ? hedefProfil
            : this.profilNoktalar;

        if (profil.length === 0) return;

        const ilkNokta = profil[0];

        // Rapid pozisyon - giriş başlangıç noktası üzerine
        const rapidPoz = new FikoVektorTakimYol3D(
            ilkNokta.x, ilkNokta.y, this.guvenliZ
        );
        rapidPoz.tip = 'rapid';
        rapidPoz.hiz = this.hizRapid;
        rapidPoz.devir = this.devir;
        rapidPoz.renk = new THREE.Color(0x00ff00); // Yeşil - rapid
        this.takimYoluNoktalar.push(rapidPoz);
    }

    /**
     * Giriş hareketi hesapla - 5 farklı yöntem
     * @param hedefZ - Hedef Z derinliği
     * @param baslangicZ - Başlangıç Z yüksekliği
     * @param hedefProfil - Hedef profil noktaları (offset'li profil)
     */
    private girisHesapla(hedefZ: number, baslangicZ: number, hedefProfil?: FikoVektor3D[]): void {
        // Offset'li profil verilmişse onu kullan
        const profil = hedefProfil && hedefProfil.length > 0
            ? hedefProfil
            : this.profilNoktalar;

        if (profil.length === 0) return;

        const ilkNokta = profil[0];
        const ikinciNokta = profil.length > 1
            ? profil[1]
            : ilkNokta;

        // Profil yönünü hesapla (teğet vektör)
        const dx = ikinciNokta.x - ilkNokta.x;
        const dy = ikinciNokta.y - ilkNokta.y;
        const uzunluk = Math.sqrt(dx * dx + dy * dy);
        const tegetX = uzunluk > 0 ? dx / uzunluk : 1;
        const tegetY = uzunluk > 0 ? dy / uzunluk : 0;

        // Normal vektör (teğete dik, sola dönük)
        const normalX = -tegetY;
        const normalY = tegetX;

        switch (this.girisYontemi) {
            case 'Dikey':
                this.girisHesaplaDikey(ilkNokta, hedefZ, baslangicZ);
                break;
            case 'Rampa':
                this.girisHesaplaRampa(ilkNokta, hedefZ, baslangicZ, tegetX, tegetY);
                break;
            case 'Helisel':
                this.girisHesaplaHelisel(ilkNokta, hedefZ, baslangicZ);
                break;
            case 'Yay':
                this.girisHesaplaYay(ilkNokta, hedefZ, tegetX, tegetY, normalX, normalY);
                break;
            case 'Teget':
                this.girisHesaplaTeget(ilkNokta, hedefZ, baslangicZ, tegetX, tegetY);
                break;
            default:
                this.girisHesaplaDikey(ilkNokta, hedefZ, baslangicZ);
        }
    }

    /**
     * Dikey giriş (Plunge) - Doğrudan Z'ye iniş
     */
    private girisHesaplaDikey(nokta: FikoVektor3D, hedefZ: number, baslangicZ: number): void {
        // Dalma hareketi
        const dalma = new FikoVektorTakimYol3D(nokta.x, nokta.y, hedefZ);
        dalma.tip = 'linear';
        dalma.hiz = this.ilerlemeDalma;
        dalma.devir = this.devir;
        dalma.renk = new THREE.Color(0xffff00); // Sarı - dalma
        this.takimYoluNoktalar.push(dalma);
    }

    /**
     * Rampa girişi - Açılı XY+Z eşli hareket
     */
    private girisHesaplaRampa(
        nokta: FikoVektor3D,
        hedefZ: number,
        baslangicZ: number,
        tegetX: number,
        tegetY: number
    ): void {
        const aciRad = this.girisRampaAcisi * Math.PI / 180;
        const zFark = baslangicZ - hedefZ;
        const xyUzunluk = zFark / Math.tan(aciRad);

        // Rampa başlangıç noktası (profil yönünün tersinde)
        const basX = nokta.x - tegetX * xyUzunluk;
        const basY = nokta.y - tegetY * xyUzunluk;

        // Rampa başlangıcına rapid
        const rampaBas = new FikoVektorTakimYol3D(basX, basY, baslangicZ);
        rampaBas.tip = 'rapid';
        rampaBas.hiz = this.hizRapid;
        rampaBas.devir = this.devir;
        rampaBas.renk = new THREE.Color(0x00ff00);
        this.takimYoluNoktalar.push(rampaBas);

        // Rampa hareketi (açılı iniş)
        const rampaSon = new FikoVektorTakimYol3D(nokta.x, nokta.y, hedefZ);
        rampaSon.tip = 'linear';
        rampaSon.hiz = this.ilerlemeDalma;
        rampaSon.devir = this.devir;
        rampaSon.renk = new THREE.Color(0xffaa00); // Turuncu - rampa
        this.takimYoluNoktalar.push(rampaSon);
    }

    /**
     * Helisel giriş - Spiral iniş
     */
    private girisHesaplaHelisel(nokta: FikoVektor3D, hedefZ: number, baslangicZ: number): void {
        const radius = this.girisHeliselCap / 2;
        const zFark = baslangicZ - hedefZ;
        const turSayisi = Math.ceil(zFark / this.girisHeliselAdim);
        const toplamAci = turSayisi * 360;
        const zAdim = zFark / toplamAci;

        // Helix merkezi = giriş noktası
        const merkezX = nokta.x;
        const merkezY = nokta.y;

        // Helix başlangıç noktasına rapid
        const helixBas = new FikoVektorTakimYol3D(
            merkezX + radius,
            merkezY,
            baslangicZ
        );
        helixBas.tip = 'rapid';
        helixBas.hiz = this.hizRapid;
        helixBas.devir = this.devir;
        helixBas.renk = new THREE.Color(0x00ff00);
        this.takimYoluNoktalar.push(helixBas);

        // Helisel spiral noktaları
        const aciAdim = this.girisYonuCW ? -15 : 15; // 15 derece adım
        let aci = 0;
        let z = baslangicZ;

        while (z > hedefZ) {
            aci += aciAdim;
            z -= Math.abs(aciAdim) * zAdim;
            if (z < hedefZ) z = hedefZ;

            const aciRad = aci * Math.PI / 180;
            const x = merkezX + radius * Math.cos(aciRad);
            const y = merkezY + radius * Math.sin(aciRad);

            const helixNokta = new FikoVektorTakimYol3D(x, y, z);
            helixNokta.tip = 'linear';
            helixNokta.hiz = this.ilerlemeDalma;
            helixNokta.devir = this.devir;
            helixNokta.renk = new THREE.Color(0xff00ff); // Magenta - helisel
            this.takimYoluNoktalar.push(helixNokta);
        }

        // Helix merkezine (giriş noktasına) doğru hareket
        const helixMerkez = new FikoVektorTakimYol3D(merkezX, merkezY, hedefZ);
        helixMerkez.tip = 'linear';
        helixMerkez.hiz = this.ilerlemeIsleme;
        helixMerkez.devir = this.devir;
        helixMerkez.renk = new THREE.Color(0x00ffff);
        this.takimYoluNoktalar.push(helixMerkez);
    }

    /**
     * Yay girişi (Arc/Roll-In) - G02/G03 ile teğet yay
     */
    private girisHesaplaYay(
        nokta: FikoVektor3D,
        hedefZ: number,
        tegetX: number,
        tegetY: number,
        normalX: number,
        normalY: number
    ): void {
        const radius = this.girisYayRadius;
        const sweepAci = this.girisYayAci * Math.PI / 180;

        // Yay yönüne göre normal yönü ayarla
        const yonFaktor = this.girisYonuCW ? 1 : -1;

        // Yay merkezi (profil noktasından normale doğru radius kadar)
        const merkezX = nokta.x + normalX * radius * yonFaktor;
        const merkezY = nokta.y + normalY * radius * yonFaktor;

        // Yay başlangıç açısı (profilden tersine)
        const basAci = Math.atan2(nokta.y - merkezY, nokta.x - merkezX);
        const sonAci = basAci + (this.girisYonuCW ? sweepAci : -sweepAci);

        // Yay başlangıç noktası
        const yayBasX = merkezX + radius * Math.cos(sonAci);
        const yayBasY = merkezY + radius * Math.sin(sonAci);

        // Yay başlangıcına rapid + dikey iniş
        const yayBas = new FikoVektorTakimYol3D(yayBasX, yayBasY, this.guvenliZ);
        yayBas.tip = 'rapid';
        yayBas.hiz = this.hizRapid;
        yayBas.devir = this.devir;
        yayBas.renk = new THREE.Color(0x00ff00);
        this.takimYoluNoktalar.push(yayBas);

        // Dikey iniş
        const yayDalma = new FikoVektorTakimYol3D(yayBasX, yayBasY, hedefZ);
        yayDalma.tip = 'linear';
        yayDalma.hiz = this.ilerlemeDalma;
        yayDalma.devir = this.devir;
        yayDalma.renk = new THREE.Color(0xffff00);
        this.takimYoluNoktalar.push(yayDalma);

        // Yay noktalarını hesapla (G02/G03 interpolasyonu)
        const yayNoktalar = this.yayNoktalarHesapla(
            merkezX, merkezY,
            radius,
            sonAci, basAci,
            hedefZ,
            this.girisYonuCW
        );

        for (const yn of yayNoktalar) {
            yn.tip = this.girisYonuCW ? 'arc_cw' : 'arc_ccw';
            yn.hiz = this.ilerlemeIsleme * (this.ilerlemeLead / 100);
            yn.devir = this.devir;
            yn.renk = new THREE.Color(0x00ffaa); // Cyan-yeşil - yay
            // I,J değerlerini ekle (G02/G03 için)
            yn.i = merkezX - yn.x;
            yn.j = merkezY - yn.y;
            this.takimYoluNoktalar.push(yn);
        }
    }

    /**
     * Teğet girişi - Profil yönünde düz yaklaşma
     */
    private girisHesaplaTeget(
        nokta: FikoVektor3D,
        hedefZ: number,
        baslangicZ: number,
        tegetX: number,
        tegetY: number
    ): void {
        const uzunluk = this.girisTegetUzunluk;

        // Teğet başlangıç noktası (profil yönünün tersinde)
        const basX = nokta.x - tegetX * uzunluk;
        const basY = nokta.y - tegetY * uzunluk;

        // Teğet başlangıcına rapid
        const tegetBas = new FikoVektorTakimYol3D(basX, basY, this.guvenliZ);
        tegetBas.tip = 'rapid';
        tegetBas.hiz = this.hizRapid;
        tegetBas.devir = this.devir;
        tegetBas.renk = new THREE.Color(0x00ff00);
        this.takimYoluNoktalar.push(tegetBas);

        // Dikey iniş
        const tegetDalma = new FikoVektorTakimYol3D(basX, basY, hedefZ);
        tegetDalma.tip = 'linear';
        tegetDalma.hiz = this.ilerlemeDalma;
        tegetDalma.devir = this.devir;
        tegetDalma.renk = new THREE.Color(0xffff00);
        this.takimYoluNoktalar.push(tegetDalma);

        // Teğet yaklaşma (düz çizgi ile profile)
        const tegetSon = new FikoVektorTakimYol3D(nokta.x, nokta.y, hedefZ);
        tegetSon.tip = 'linear';
        tegetSon.hiz = this.ilerlemeIsleme * (this.ilerlemeLead / 100);
        tegetSon.devir = this.devir;
        tegetSon.renk = new THREE.Color(0x88ffff); // Açık cyan - teğet
        this.takimYoluNoktalar.push(tegetSon);
    }

    // ═══════════════════════════════════════════════════════════════════
    // PROFİL TURU HESAPLAMA
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Tek profil turu hesapla - Ana profil ile
     * @deprecated Yeni sistemde profilTuruHesaplaIle() kullanın
     */
    private profilTuruHesapla(z: number): void {
        const profil = this.profilNoktalar;

        for (let i = 0; i < profil.length; i++) {
            const nokta = profil[i];
            const takimNokta = new FikoVektorTakimYol3D(nokta.x, nokta.y, z);
            takimNokta.tip = 'linear';
            takimNokta.hiz = this.ilerlemeIsleme;
            takimNokta.devir = this.devir;
            takimNokta.renk = new THREE.Color(0x00ffff); // Cyan - işleme
            this.takimYoluNoktalar.push(takimNokta);
        }

        // Kapalı profil ise ilk noktaya dön
        if (this.profilKapaliMi()) {
            const ilkNokta = profil[0];
            const kapanisNokta = new FikoVektorTakimYol3D(ilkNokta.x, ilkNokta.y, z);
            kapanisNokta.tip = 'linear';
            kapanisNokta.hiz = this.ilerlemeIsleme;
            kapanisNokta.devir = this.devir;
            kapanisNokta.renk = new THREE.Color(0x00ffff);
            this.takimYoluNoktalar.push(kapanisNokta);
        }
    }

    /**
     * Overlap hesapla (profil başına ek hareket)
     * @deprecated Yeni sistemde overlapHesaplaIle() kullanın
     */
    private overlapHesapla(z: number): void {
        const profil = this.profilNoktalar;

        if (profil.length < 2 || this.overlap <= 0) return;

        // Overlap yüzdesi kadar profil boyunca ilerle
        const toplamNokta = profil.length;
        const overlapNokta = Math.ceil(toplamNokta * (this.overlap / 100));

        for (let i = 1; i <= overlapNokta && i < profil.length; i++) {
            const nokta = profil[i];
            const takimNokta = new FikoVektorTakimYol3D(nokta.x, nokta.y, z);
            takimNokta.tip = 'linear';
            takimNokta.hiz = this.ilerlemeIsleme;
            takimNokta.devir = this.devir;
            takimNokta.renk = new THREE.Color(0x00aaff); // Mavi - overlap
            this.takimYoluNoktalar.push(takimNokta);
        }
    }

    /**
     * Ara çıkış hesapla (seviyeler arası)
     */
    private araCikisHesapla(mevcutZ: number): void {
        if (this.takimYoluNoktalar.length === 0) return;

        const sonNokta = this.takimYoluNoktalar[this.takimYoluNoktalar.length - 1];

        // Ara geçiş yüksekliğine çık
        const araCikis = new FikoVektorTakimYol3D(
            sonNokta.x, sonNokta.y, mevcutZ + this.araGecisZ
        );
        araCikis.tip = 'linear';
        araCikis.hiz = this.ilerlemeCekme;
        araCikis.devir = this.devir;
        araCikis.renk = new THREE.Color(0xffff00); // Sarı - ara çıkış
        this.takimYoluNoktalar.push(araCikis);
    }

    /**
     * Son tur (finish) hesapla - Son tur payı ile tam Z'de
     * Finish profili: Temel offset ile hesaplanır (roughing stock payı olmadan)
     */
    private sonTurHesapla(): void {
        const finishZ = this.altZ;

        // Finish profili için offset hesapla (sadece takım yarıçapı kompanzasyonu)
        const finishOffset = this.hesaplaFinishOfset();
        const profil = this.hesaplaOfsetProfilIle(finishOffset);

        if (profil.length === 0) return;

        // Son tur için ayrı giriş kullanılacaksa
        if (this.sonTurAyriGiris) {
            // Güvenli Z'ye çık
            const sonNokta = this.takimYoluNoktalar[this.takimYoluNoktalar.length - 1];
            const guvenliCikis = new FikoVektorTakimYol3D(sonNokta.x, sonNokta.y, this.guvenliZ);
            guvenliCikis.tip = 'rapid';
            guvenliCikis.hiz = this.hizRapid;
            guvenliCikis.devir = this.devir;
            guvenliCikis.renk = new THREE.Color(0x00ff00);
            this.takimYoluNoktalar.push(guvenliCikis);

            // İlk noktaya rapid
            const ilkNokta = profil[0];
            const finishRapid = new FikoVektorTakimYol3D(ilkNokta.x, ilkNokta.y, this.guvenliZ);
            finishRapid.tip = 'rapid';
            finishRapid.hiz = this.hizRapid;
            finishRapid.devir = this.sonTurDevir;
            finishRapid.renk = new THREE.Color(0x00ff00);
            this.takimYoluNoktalar.push(finishRapid);

            // Finish Z'ye dalma
            const finishDalma = new FikoVektorTakimYol3D(ilkNokta.x, ilkNokta.y, finishZ);
            finishDalma.tip = 'linear';
            finishDalma.hiz = this.ilerlemeDalma;
            finishDalma.devir = this.sonTurDevir;
            finishDalma.renk = new THREE.Color(0xffff00);
            this.takimYoluNoktalar.push(finishDalma);
        } else {
            // Mevcut konumdan finish Z'ye doğru dalma (ilk nokta zaten profilden)
            const dalma = new FikoVektorTakimYol3D(profil[0].x, profil[0].y, finishZ);
            dalma.tip = 'linear';
            dalma.hiz = this.ilerlemeDalma;
            dalma.devir = this.sonTurDevir;
            dalma.renk = new THREE.Color(0xffff00);
            this.takimYoluNoktalar.push(dalma);
        }

        // Son tur profili
        for (const nokta of profil) {
            const takimNokta = new FikoVektorTakimYol3D(nokta.x, nokta.y, finishZ);
            takimNokta.tip = 'linear';
            takimNokta.hiz = this.sonTurIlerleme;
            takimNokta.devir = this.sonTurDevir;
            takimNokta.renk = new THREE.Color(0xff00ff); // Magenta - finish
            this.takimYoluNoktalar.push(takimNokta);
        }

        // Kapalı profil ise ilk noktaya dön
        if (this.profilKapaliMi()) {
            const ilkNokta = profil[0];
            const kapanisNokta = new FikoVektorTakimYol3D(ilkNokta.x, ilkNokta.y, finishZ);
            kapanisNokta.tip = 'linear';
            kapanisNokta.hiz = this.sonTurIlerleme;
            kapanisNokta.devir = this.sonTurDevir;
            kapanisNokta.renk = new THREE.Color(0xff00ff);
            this.takimYoluNoktalar.push(kapanisNokta);
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // ÇIKIŞ HAREKETİ HESAPLAMA (3 YÖNTEM)
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Çıkış hareketi hesapla - 3 farklı yöntem
     * Profil yönünü ana profilNoktalar'dan alır (çıkış yönü hesaplaması için)
     */
    private cikisHesapla(): void {
        if (this.takimYoluNoktalar.length === 0) return;

        const sonNokta = this.takimYoluNoktalar[this.takimYoluNoktalar.length - 1];
        const profil = this.profilNoktalar;

        // Profil yönünü hesapla (çıkış için)
        let tegetX = 1, tegetY = 0, normalX = 0, normalY = 1;
        if (profil.length >= 2) {
            const sonIdx = profil.length - 1;
            const dx = profil[sonIdx].x - profil[sonIdx > 0 ? sonIdx - 1 : 0].x;
            const dy = profil[sonIdx].y - profil[sonIdx > 0 ? sonIdx - 1 : 0].y;
            const uzunluk = Math.sqrt(dx * dx + dy * dy);
            if (uzunluk > 0) {
                tegetX = dx / uzunluk;
                tegetY = dy / uzunluk;
                normalX = -tegetY;
                normalY = tegetX;
            }
        }

        switch (this.cikisYontemi) {
            case 'Dikey':
                this.cikisHesaplaDikey(sonNokta);
                break;
            case 'Rampa':
                this.cikisHesaplaRampa(sonNokta, tegetX, tegetY);
                break;
            case 'Yay':
                this.cikisHesaplaYay(sonNokta, tegetX, tegetY, normalX, normalY);
                break;
            default:
                this.cikisHesaplaDikey(sonNokta);
        }
    }

    /**
     * Dikey çıkış - Doğrudan Z yükselmesi
     */
    private cikisHesaplaDikey(sonNokta: FikoVektorTakimYol3D): void {
        // Güvenli Z'ye çık
        const cikis = new FikoVektorTakimYol3D(sonNokta.x, sonNokta.y, this.guvenliZ);
        cikis.tip = 'rapid';
        cikis.hiz = this.hizRapid;
        cikis.devir = this.devir;
        cikis.renk = new THREE.Color(0xff0000); // Kırmızı - çıkış
        this.takimYoluNoktalar.push(cikis);
    }

    /**
     * Rampa çıkışı - Açılı yükselme
     */
    private cikisHesaplaRampa(sonNokta: FikoVektorTakimYol3D, tegetX: number, tegetY: number): void {
        const aciRad = this.cikisRampaAcisi * Math.PI / 180;
        const zFark = this.guvenliZ - sonNokta.z;
        const xyUzunluk = zFark / Math.tan(aciRad);

        // Rampa bitiş noktası (profil yönünde)
        const bitisX = sonNokta.x + tegetX * xyUzunluk;
        const bitisY = sonNokta.y + tegetY * xyUzunluk;

        // Rampa hareketi
        const rampaCikis = new FikoVektorTakimYol3D(bitisX, bitisY, this.guvenliZ);
        rampaCikis.tip = 'linear';
        rampaCikis.hiz = this.ilerlemeCekme;
        rampaCikis.devir = this.devir;
        rampaCikis.renk = new THREE.Color(0xffaa00); // Turuncu - rampa çıkış
        this.takimYoluNoktalar.push(rampaCikis);
    }

    /**
     * Yay çıkışı (Arc/Roll-Out) - G02/G03 ile teğet yay
     */
    private cikisHesaplaYay(
        sonNokta: FikoVektorTakimYol3D,
        tegetX: number,
        tegetY: number,
        normalX: number,
        normalY: number
    ): void {
        const radius = this.cikisYayRadius;
        const sweepAci = this.cikisYayAci * Math.PI / 180;

        // Yay yönüne göre normal yönü ayarla
        const yonFaktor = this.cikisYonuCW ? 1 : -1;

        // Yay merkezi
        const merkezX = sonNokta.x + normalX * radius * yonFaktor;
        const merkezY = sonNokta.y + normalY * radius * yonFaktor;

        // Yay açıları
        const basAci = Math.atan2(sonNokta.y - merkezY, sonNokta.x - merkezX);
        const sonAci = basAci + (this.cikisYonuCW ? -sweepAci : sweepAci);

        // Yay noktalarını hesapla
        const yayNoktalar = this.yayNoktalarHesapla(
            merkezX, merkezY,
            radius,
            basAci, sonAci,
            sonNokta.z,
            !this.cikisYonuCW
        );

        for (const yn of yayNoktalar) {
            yn.tip = this.cikisYonuCW ? 'arc_cw' : 'arc_ccw';
            yn.hiz = this.ilerlemeCekme;
            yn.devir = this.devir;
            yn.renk = new THREE.Color(0xffaa88); // Açık turuncu - yay çıkış
            yn.i = merkezX - yn.x;
            yn.j = merkezY - yn.y;
            this.takimYoluNoktalar.push(yn);
        }

        // Son noktadan güvenli Z'ye dikey çıkış
        const sonYayNokta = yayNoktalar[yayNoktalar.length - 1] || sonNokta;
        const dikeyZCikis = new FikoVektorTakimYol3D(sonYayNokta.x, sonYayNokta.y, this.guvenliZ);
        dikeyZCikis.tip = 'rapid';
        dikeyZCikis.hiz = this.hizRapid;
        dikeyZCikis.devir = this.devir;
        dikeyZCikis.renk = new THREE.Color(0xff0000);
        this.takimYoluNoktalar.push(dikeyZCikis);
    }

    // ═══════════════════════════════════════════════════════════════════
    // YAY (ARC) İNTERPOLASYONU - G02/G03
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Yay noktalarını hesapla (G02/G03 için)
     */
    private yayNoktalarHesapla(
        merkezX: number,
        merkezY: number,
        radius: number,
        basAci: number,
        sonAci: number,
        z: number,
        ccw: boolean
    ): FikoVektorTakimYol3D[] {
        const noktalar: FikoVektorTakimYol3D[] = [];

        // Açı farkını hesapla
        let aciFark = sonAci - basAci;

        // CCW/CW yönüne göre düzelt
        if (ccw && aciFark < 0) aciFark += 2 * Math.PI;
        if (!ccw && aciFark > 0) aciFark -= 2 * Math.PI;

        // Interpolasyon adımı (toleransa göre)
        const aciAdim = Math.abs(aciFark) / Math.max(1, Math.ceil(Math.abs(aciFark) / (15 * Math.PI / 180)));

        let aci = basAci;
        const adimYon = aciFark > 0 ? aciAdim : -aciAdim;

        while ((aciFark > 0 && aci < sonAci) || (aciFark < 0 && aci > sonAci)) {
            aci += adimYon;

            // Son açıyı aşmamak için kontrol
            if ((aciFark > 0 && aci > sonAci) || (aciFark < 0 && aci < sonAci)) {
                aci = sonAci;
            }

            const x = merkezX + radius * Math.cos(aci);
            const y = merkezY + radius * Math.sin(aci);

            noktalar.push(new FikoVektorTakimYol3D(x, y, z));
        }

        return noktalar;
    }

    /**
     * Profil kapalı mı kontrol et
     * Ana profilNoktalar'dan kontrol edilir (offset sonrası değişmez)
     */
    private profilKapaliMi(): boolean {
        if (this.profilKapali !== null) return this.profilKapali;   // güvenilir kaynak (yüz seçimi → true)
        const profil = this.profilNoktalar;

        if (profil.length < 3) return false;

        const ilk = profil[0];
        const son = profil[profil.length - 1];

        const mesafe = Math.sqrt(
            Math.pow(son.x - ilk.x, 2) +
            Math.pow(son.y - ilk.y, 2)
        );

        return mesafe < 0.01; // 0.01mm tolerans
    }

    // ═══════════════════════════════════════════════════════════════════
    // TAB (KÖPRÜ) HESAPLAMA - Parça Sabitleme
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Tab pozisyonlarını hesapla
     * Profil boyunca eşit aralıklarla tab noktaları belirler
     */
    private hesaplaTabPozisyonlari(profil: FikoVektor3D[]): number[] {
        if (!this.tabEtkin || profil.length < 2) return [];

        // Toplam profil uzunluğunu hesapla
        let toplamUzunluk = 0;
        const segmentUzunluklari: number[] = [];

        for (let i = 1; i < profil.length; i++) {
            const dx = profil[i].x - profil[i - 1].x;
            const dy = profil[i].y - profil[i - 1].y;
            const uzunluk = Math.sqrt(dx * dx + dy * dy);
            segmentUzunluklari.push(uzunluk);
            toplamUzunluk += uzunluk;
        }

        // Tab pozisyonlarını hesapla
        const tabPozisyonlari: number[] = [];

        if (this.tabDagitim === 'Sayi') {
            // Eşit sayıda tab dağıt
            const aralik = toplamUzunluk / (this.tabSayisi + 1);
            for (let i = 1; i <= this.tabSayisi; i++) {
                tabPozisyonlari.push(aralik * i);
            }
        } else {
            // Mesafeye göre tab dağıt
            let mesafe = this.tabMesafe;
            while (mesafe < toplamUzunluk - this.tabMesafe / 2) {
                tabPozisyonlari.push(mesafe);
                mesafe += this.tabMesafe;
            }
        }

        return tabPozisyonlari;
    }

    /**
     * Tab noktasında Z yükselmesi uygula
     * Tab şekline göre (dikdörtgen veya üçgen) Z profilini değiştirir
     */
    private tabZHesapla(
        profilMesafe: number,
        tabPozisyonlari: number[],
        altZ: number
    ): number {
        if (!this.tabEtkin || tabPozisyonlari.length === 0) return altZ;

        const tabYariGenislik = this.tabGenislik / 2;

        for (const tabPoz of tabPozisyonlari) {
            const mesafeFark = Math.abs(profilMesafe - tabPoz);

            if (mesafeFark <= tabYariGenislik) {
                if (this.tabSekli === 'Dikdortgen') {
                    // Dikdörtgen tab - düz yükselme
                    return altZ + this.tabYukseklik;
                } else {
                    // Üçgen tab - kademeli yükselme
                    const oran = 1 - (mesafeFark / tabYariGenislik);
                    return altZ + (this.tabYukseklik * oran);
                }
            }
        }

        return altZ;
    }

    // ═══════════════════════════════════════════════════════════════════
    // KÖŞE YAVAŞLAMA (Corner Slowdown)
    // ═══════════════════════════════════════════════════════════════════

    /**
     * İki vektör arasındaki açıyı hesapla (derece)
     */
    private vektorAcisiHesapla(v1x: number, v1y: number, v2x: number, v2y: number): number {
        const dot = v1x * v2x + v1y * v2y;
        const mag1 = Math.sqrt(v1x * v1x + v1y * v1y);
        const mag2 = Math.sqrt(v2x * v2x + v2y * v2y);

        if (mag1 < 0.0001 || mag2 < 0.0001) return 180;

        const cosAci = Math.max(-1, Math.min(1, dot / (mag1 * mag2)));
        return Math.acos(cosAci) * 180 / Math.PI;
    }

    /**
     * Köşe tipini belirle (iç veya dış köşe)
     */
    private koseTipiBelirle(
        oncekiX: number, oncekiY: number,
        mevcutX: number, mevcutY: number,
        sonrakiX: number, sonrakiY: number
    ): 'Ic' | 'Dis' {
        // Cross product ile yön belirle
        const v1x = mevcutX - oncekiX;
        const v1y = mevcutY - oncekiY;
        const v2x = sonrakiX - mevcutX;
        const v2y = sonrakiY - mevcutY;

        const cross = v1x * v2y - v1y * v2x;

        // Ofset yönüne göre iç/dış belirleme
        if (this.ofsetYonu === 'Sol') {
            return cross > 0 ? 'Dis' : 'Ic';
        } else {
            return cross < 0 ? 'Dis' : 'Ic';
        }
    }

    /**
     * Köşe yavaşlama hızını hesapla
     */
    private koseHiziHesapla(
        oncekiNokta: FikoVektor3D | null,
        mevcutNokta: FikoVektor3D,
        sonrakiNokta: FikoVektor3D | null,
        temelHiz: number
    ): number {
        if (!this.koseYavaslamaEtkin || !oncekiNokta || !sonrakiNokta) {
            return temelHiz;
        }

        // Vektörleri hesapla
        const v1x = mevcutNokta.x - oncekiNokta.x;
        const v1y = mevcutNokta.y - oncekiNokta.y;
        const v2x = sonrakiNokta.x - mevcutNokta.x;
        const v2y = sonrakiNokta.y - mevcutNokta.y;

        // Açıyı hesapla
        const aci = this.vektorAcisiHesapla(v1x, v1y, v2x, v2y);

        // Minimum açıdan küçükse yavaşlama uygulama
        if (aci >= this.koseMinAci) {
            return temelHiz;
        }

        // Köşe tipini kontrol et
        if (this.koseYavaslamaTipi !== 'Tum') {
            const koseTipi = this.koseTipiBelirle(
                oncekiNokta.x, oncekiNokta.y,
                mevcutNokta.x, mevcutNokta.y,
                sonrakiNokta.x, sonrakiNokta.y
            );

            if (this.koseYavaslamaTipi !== koseTipi) {
                return temelHiz;
            }
        }

        // Yavaşlama oranını uygula (açıya göre kademeli)
        const yavaslamaFaktor = this.koseYavaslamaOrani / 100;
        const aciOrani = 1 - (aci / this.koseMinAci);
        const uygulanacakYavaslama = yavaslamaFaktor * aciOrani;

        return temelHiz * (1 - uygulanacakYavaslama);
    }

    // ═══════════════════════════════════════════════════════════════════
    // YOL YUMUŞATMA (Path Smoothing)
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Profil noktalarını yumuşat
     * Keskin köşeleri belirtilen radius ile yuvarlar
     */
    private yumusatProfil(profil: FikoVektor3D[]): FikoVektor3D[] {
        if (!this.yumusatmaEtkin || profil.length < 3) {
            return profil;
        }

        const yumusatilmis: FikoVektor3D[] = [];
        yumusatilmis.push(profil[0]); // İlk noktayı koru

        for (let i = 1; i < profil.length - 1; i++) {
            const onceki = profil[i - 1];
            const mevcut = profil[i];
            const sonraki = profil[i + 1];

            // Açıyı hesapla
            const v1x = mevcut.x - onceki.x;
            const v1y = mevcut.y - onceki.y;
            const v2x = sonraki.x - mevcut.x;
            const v2y = sonraki.y - mevcut.y;

            const aci = this.vektorAcisiHesapla(v1x, v1y, v2x, v2y);

            // Açı keskinse yumuşat
            if (aci < 170) {
                // Yumuşatma noktaları ekle
                const mag1 = Math.sqrt(v1x * v1x + v1y * v1y);
                const mag2 = Math.sqrt(v2x * v2x + v2y * v2y);

                const blend = Math.min(this.yumusatmaRadius, mag1 / 3, mag2 / 3);

                // Köşe öncesi nokta
                const oncesiX = mevcut.x - (v1x / mag1) * blend;
                const oncesiY = mevcut.y - (v1y / mag1) * blend;
                yumusatilmis.push(new FikoVektor3D(oncesiX, oncesiY, mevcut.z));

                // Köşe sonrası nokta
                const sonrasiX = mevcut.x + (v2x / mag2) * blend;
                const sonrasiY = mevcut.y + (v2y / mag2) * blend;
                yumusatilmis.push(new FikoVektor3D(sonrasiX, sonrasiY, mevcut.z));
            } else {
                yumusatilmis.push(mevcut);
            }
        }

        yumusatilmis.push(profil[profil.length - 1]); // Son noktayı koru

        return yumusatilmis;
    }

    // ═══════════════════════════════════════════════════════════════════
    // STAY-DOWN / LIFT KONTROLÜ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * İki nokta arası mesafeyi hesapla
     */
    private mesafeHesapla(n1: FikoVektor3D, n2: FikoVektor3D): number {
        const dx = n2.x - n1.x;
        const dy = n2.y - n1.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Stay-down veya retract kararı ver
     * Mesafe kısaysa malzeme üzerinde kalır, uzunsa yukarı çıkar
     */
    private stayDownKarari(
        sonNokta: FikoVektor3D,
        hedefNokta: FikoVektor3D,
        mevcutZ: number
    ): FikoVektorTakimYol3D[] {
        const hareketler: FikoVektorTakimYol3D[] = [];
        const mesafe = this.mesafeHesapla(sonNokta, hedefNokta);

        if (this.stayDownEtkin && mesafe <= this.stayDownMesafe) {
            // Stay-down: Malzeme üzerinde kal, sadece lift mesafesi kadar kalk
            const liftZ = mevcutZ + this.kaldirmaMesafesi;

            // Hafif kalkış
            const lift = new FikoVektorTakimYol3D(sonNokta.x, sonNokta.y, liftZ);
            lift.tip = 'linear';
            lift.hiz = this.ilerlemeCekme;
            lift.devir = this.devir;
            lift.renk = new THREE.Color(0xffff00);
            hareketler.push(lift);

            // Hedefe hareket
            const gecis = new FikoVektorTakimYol3D(hedefNokta.x, hedefNokta.y, liftZ);
            gecis.tip = 'linear';
            gecis.hiz = this.ilerlemeIsleme;
            gecis.devir = this.devir;
            gecis.renk = new THREE.Color(0xffaa00);
            hareketler.push(gecis);

            // Tekrar iniş
            const inis = new FikoVektorTakimYol3D(hedefNokta.x, hedefNokta.y, mevcutZ);
            inis.tip = 'linear';
            inis.hiz = this.ilerlemeDalma;
            inis.devir = this.devir;
            inis.renk = new THREE.Color(0xffff00);
            hareketler.push(inis);
        } else {
            // Full retract: Güvenli Z'ye çık
            const retract = new FikoVektorTakimYol3D(sonNokta.x, sonNokta.y, this.guvenliZ);
            retract.tip = 'rapid';
            retract.hiz = this.hizRapid;
            retract.devir = this.devir;
            retract.renk = new THREE.Color(0x00ff00);
            hareketler.push(retract);

            // Hedefe rapid
            const rapid = new FikoVektorTakimYol3D(hedefNokta.x, hedefNokta.y, this.guvenliZ);
            rapid.tip = 'rapid';
            rapid.hiz = this.hizRapid;
            rapid.devir = this.devir;
            rapid.renk = new THREE.Color(0x00ff00);
            hareketler.push(rapid);

            // Dalma
            const dalma = new FikoVektorTakimYol3D(hedefNokta.x, hedefNokta.y, mevcutZ);
            dalma.tip = 'linear';
            dalma.hiz = this.ilerlemeDalma;
            dalma.devir = this.devir;
            dalma.renk = new THREE.Color(0xffff00);
            hareketler.push(dalma);
        }

        return hareketler;
    }

    // ═══════════════════════════════════════════════════════════════════
    // FREZE YÖNÜ (Climb/Conventional) HESAPLAMA
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Freze yönüne göre ofset yönünü belirle
     * Climb (Tırmanma): Dış profil = Sol, İç profil = Sağ
     * Conventional: Dış profil = Sağ, İç profil = Sol
     */
    private frezeYonuOfsetHesapla(): 'Sol' | 'Sag' {
        if (this.ofsetYonu === 'Merkez') return 'Merkez' as any;

        const disProfil = this.ofsetYonu === 'Sol' || this.ofsetYonu === 'Sag';

        if (this.frezeYonu === 'Tirmanma') {
            // Climb milling
            return disProfil ? 'Sol' : 'Sag';
        } else {
            // Conventional milling
            return disProfil ? 'Sag' : 'Sol';
        }
    }

    /**
     * Takım yolu görselini güncelle
     */
    private guncelleTakimYoluGorsel(): void {
        // Eski çizgiyi kaldır
        if (this.takimYoluLine) {
            this.remove(this.takimYoluLine);
            this.takimYoluLine.geometry.dispose();
            (this.takimYoluLine.material as THREE.Material).dispose();
        }

        if (this.takimYoluNoktalar.length < 2) return;

        // Geçerli noktaları filtrele (NaN ve undefined değerleri çıkar)
        const gecerliNoktalar = this.takimYoluNoktalar.filter(n =>
            n != null &&
            typeof n.x === 'number' && !isNaN(n.x) && isFinite(n.x) &&
            typeof n.y === 'number' && !isNaN(n.y) && isFinite(n.y) &&
            typeof n.z === 'number' && !isNaN(n.z) && isFinite(n.z)
        );

        if (gecerliNoktalar.length < 2) {
            console.warn('FikoAktiviteProfil: Yeterli geçerli nokta yok, takım yolu görseli oluşturulamadı.');
            return;
        }

        // Yeni geometri oluştur
        const noktalar = gecerliNoktalar.map(n =>
            new THREE.Vector3(n.x, n.y, n.z)
        );

        const geometry = new THREE.BufferGeometry().setFromPoints(noktalar);

        // Renkleri ayarla (varsayılan renk: beyaz)
        const varsayilanRenk = new THREE.Color(1, 1, 1);
        const renkler: number[] = [];
        for (const nokta of gecerliNoktalar) {
            const renk = nokta.renk || varsayilanRenk;
            renkler.push(renk.r ?? 1, renk.g ?? 1, renk.b ?? 1);
        }
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(renkler, 3));

        const material = new THREE.LineBasicMaterial({
            vertexColors: true,
            linewidth: 2
        });

        this.takimYoluLine = new THREE.Line(geometry, material);
        this.add(this.takimYoluLine);

        // Sahneyi güncelle
        if (this.sahne?.anaGosterim) {
            this.sahne.anaGosterim.ciz?.();
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // G-CODE ÜRETİMİ
    // ═══════════════════════════════════════════════════════════════════

    /**
     * G-code üret - Profesyonel CNC çıktısı
     */
    uretGCode(): string {
        const gcode: string[] = [];
        const precision = 4;

        // Program başlığı
        gcode.push('%');
        gcode.push(`O${Math.floor(Math.random() * 9000) + 1000} (FikoYT CAM - ${this.name})`);
        gcode.push('');
        gcode.push('(═══════════════════════════════════════════════════)');
        gcode.push('(FikoYT Professional CAM System)');
        gcode.push(`(Aktivite: ${this.name})`);
        gcode.push(`(Takım Çapı: ${this.takim.cap}mm)`);
        gcode.push(`(Tarih: ${new Date().toLocaleString('tr-TR')})`);
        gcode.push('(═══════════════════════════════════════════════════)');
        gcode.push('');

        // Başlangıç kodları
        gcode.push('G21 (Metrik birimler)');
        gcode.push('G90 (Mutlak koordinatlar)');
        gcode.push('G17 (XY düzlemi seçimi)');
        gcode.push('G40 (Takım çapı kompanzasyonu iptal)');
        gcode.push('G49 (Takım boyu kompanzasyonu iptal)');
        gcode.push('G80 (Sabit çevrim iptal)');
        gcode.push('');

        // Spindle başlat
        gcode.push(`S${this.devir} M03 (Spindle saat yönü)`);
        gcode.push('M08 (Soğutucu açık)');
        gcode.push('');

        // Güvenli yüksekliğe git
        gcode.push(`G00 Z${this.guvenliZ.toFixed(precision)} (Güvenli Z)`);
        gcode.push('');

        // Takım yolu noktaları
        let sonHiz = 0;
        let sonDevir = this.devir;

        for (let i = 0; i < this.takimYoluNoktalar.length; i++) {
            const nokta = this.takimYoluNoktalar[i];

            // Geçersiz noktaları atla
            if (!nokta || isNaN(nokta.x) || isNaN(nokta.y) || isNaN(nokta.z)) {
                console.warn(`[FikoCAM] Geçersiz nokta atlandı: index=${i}`);
                continue;
            }

            const x = nokta.x.toFixed(precision);
            const y = nokta.y.toFixed(precision);
            const z = nokta.z.toFixed(precision);

            // Devir değişikliği
            if (nokta.devir && nokta.devir !== sonDevir) {
                gcode.push(`S${Math.round(nokta.devir)}`);
                sonDevir = nokta.devir;
            }

            // Hareket tipi
            switch (nokta.tip) {
                case 'rapid':
                    gcode.push(`G00 X${x} Y${y} Z${z}`);
                    break;

                case 'linear':
                    let linearCmd = `G01 X${x} Y${y} Z${z}`;
                    if (nokta.hiz && nokta.hiz !== sonHiz) {
                        linearCmd += ` F${Math.round(nokta.hiz)}`;
                        sonHiz = nokta.hiz;
                    }
                    gcode.push(linearCmd);
                    break;

                case 'arc_cw':
                    // G02 - Saat yönü yay
                    // I,J değerleri geçerli mi kontrol et
                    if (nokta.i !== undefined && nokta.j !== undefined &&
                        !isNaN(nokta.i) && !isNaN(nokta.j) && isFinite(nokta.i) && isFinite(nokta.j)) {
                        let arcCmd = `G02 X${x} Y${y} I${nokta.i.toFixed(precision)} J${nokta.j.toFixed(precision)}`;
                        if (nokta.hiz && nokta.hiz !== sonHiz) {
                            arcCmd += ` F${Math.round(nokta.hiz)}`;
                            sonHiz = nokta.hiz;
                        }
                        gcode.push(arcCmd);
                    } else {
                        // I,J geçersiz veya yoksa linear olarak yaz
                        let linearCmd = `G01 X${x} Y${y} Z${z}`;
                        if (nokta.hiz && nokta.hiz !== sonHiz) {
                            linearCmd += ` F${Math.round(nokta.hiz)}`;
                            sonHiz = nokta.hiz;
                        }
                        gcode.push(linearCmd);
                    }
                    break;

                case 'arc_ccw':
                    // G03 - Saat yönü tersine yay
                    // I,J değerleri geçerli mi kontrol et
                    if (nokta.i !== undefined && nokta.j !== undefined &&
                        !isNaN(nokta.i) && !isNaN(nokta.j) && isFinite(nokta.i) && isFinite(nokta.j)) {
                        let arcCmd = `G03 X${x} Y${y} I${nokta.i.toFixed(precision)} J${nokta.j.toFixed(precision)}`;
                        if (nokta.hiz && nokta.hiz !== sonHiz) {
                            arcCmd += ` F${Math.round(nokta.hiz)}`;
                            sonHiz = nokta.hiz;
                        }
                        gcode.push(arcCmd);
                    } else {
                        let linearCmd = `G01 X${x} Y${y} Z${z}`;
                        if (nokta.hiz && nokta.hiz !== sonHiz) {
                            linearCmd += ` F${Math.round(nokta.hiz)}`;
                            sonHiz = nokta.hiz;
                        }
                        gcode.push(linearCmd);
                    }
                    break;

                default:
                    // Varsayılan - linear
                    let defCmd = `G01 X${x} Y${y} Z${z}`;
                    if (nokta.hiz && nokta.hiz !== sonHiz) {
                        defCmd += ` F${Math.round(nokta.hiz)}`;
                        sonHiz = nokta.hiz;
                    }
                    gcode.push(defCmd);
            }
        }

        // Program sonu
        gcode.push('');
        gcode.push(`G00 Z${this.guvenliZ.toFixed(precision)} (Güvenli Z'ye çık)`);
        gcode.push('M09 (Soğutucu kapalı)');
        gcode.push('M05 (Spindle durdur)');
        gcode.push('G28 G91 Z0 (Z referansa git)');
        gcode.push('G28 X0 Y0 (XY referansa git)');
        gcode.push('G90 (Mutlak moda geri dön)');
        gcode.push('M30 (Program sonu)');
        gcode.push('%');

        return gcode.join('\n');
    }

    /**
     * APT kodu üret (CLData formatı)
     */
    uretAPT(): string {
        let apt = '';
        apt += `$$ ═══════════════════════════════════════════════════\n`;
        apt += `$$ FikoYT Professional CAM System - APT Output\n`;
        apt += `$$ Aktivite: ${this.name}\n`;
        apt += `$$ Tarih: ${new Date().toLocaleString('tr-TR')}\n`;
        apt += `$$ ═══════════════════════════════════════════════════\n\n`;

        apt += `PARTNO / ${this.name}\n`;
        apt += `MACHIN / MILL, 3\n`;
        apt += `UNITS / MM\n\n`;

        apt += `LOADTL / 1, LENGTH, ${this.takim.boy || 50}, OSETNO, 1\n`;
        apt += `SPINDL / ${this.devir}, RPM, CLW\n`;
        apt += `COOLNT / ON\n\n`;

        apt += `RAPID\n`;
        apt += `GOTO / ${this.profilNoktalar[0]?.x.toFixed(4) || 0}, ${this.profilNoktalar[0]?.y.toFixed(4) || 0}, ${this.guvenliZ.toFixed(4)}\n\n`;

        for (const nokta of this.takimYoluNoktalar) {
            if (nokta.tip === 'rapid') {
                apt += `RAPID\n`;
            }
            apt += `GOTO / ${nokta.x.toFixed(4)}, ${nokta.y.toFixed(4)}, ${nokta.z.toFixed(4)}\n`;
            if (nokta.tip !== 'rapid') {
                apt += `FEDRAT / ${nokta.hiz}, MMPM\n`;
            }
        }

        apt += `\nRAPID\n`;
        apt += `GOTO / ${this.profilNoktalar[0]?.x.toFixed(4) || 0}, ${this.profilNoktalar[0]?.y.toFixed(4) || 0}, ${this.guvenliZ.toFixed(4)}\n`;
        apt += `COOLNT / OFF\n`;
        apt += `SPINDL / OFF\n`;
        apt += `END\n`;
        apt += `FINI\n`;

        return apt;
    }

    /** Bu aktivite için açık dialog referansı */
    private _acikDialog?: any;

    /** Duplicate dialog açmayı önlemek için kilit */
    private _dialogAciliyor = false;

    /**
     * Düzenle dialogunu aç
     */
    async duzenle($event?: any): Promise<void> {
        // Eğer dialog açma işlemi devam ediyorsa, tekrar çağırmayı engelle
        if (this._dialogAciliyor) {
            return;
        }

        if (!this.sahne?.anaGosterim?.DialogFiko) {
            console.warn('[FikoAktiviteProfil] DialogFiko servisi bulunamadı');
            return;
        }

        const dialogId = 'fiko-profil-edit-' + this.uuid;
        const dialogService = this.sahne.anaGosterim.DialogFiko;

        // 1. Önce _acikDialog referansını kontrol et
        if (this._acikDialog && this._acikDialog.miAcik) {
            // Dialog hala açık, öne getir
            const dialogEl = document.getElementById(this._acikDialog.id_dialog);
            if (dialogEl) {
                dialogEl.style.setProperty('z-index', '99999', 'important');
                dialogEl.parentElement?.style.setProperty('z-index', '99999', 'important');
            }
            return;
        }

        // 2. DialogFiko servisinde aynı ID'li dialog var mı kontrol et
        const mevcutDialog = dialogService.idIleAl?.(dialogId);
        if (mevcutDialog && mevcutDialog.miAcik) {
            this._acikDialog = mevcutDialog;
            return;
        }

        // 3. Referansı temizle
        this._acikDialog = undefined;

        // 4. Kilitle
        this._dialogAciliyor = true;

        // Dialog component'i dinamik olarak import et
        const { DialogFikoProfilComponent } = await import('./dialog-fiko-profil');

        const dialog = dialogService.ac({
            bilesen: DialogFikoProfilComponent,
            id: dialogId,
            $event: $event,
            ayarlar: {
                baslik: 'Profil Düzenle: ' + this.name,
                simge: 'pencil',
                modalMi: false,
                sinirlandi: false,
                boyut: { w: 400, h: 'auto' }
            },
            parametreler: {
                anaGosterim: this.sahne.anaGosterim,
                sahne: this.sahne,
                aktivite: this
            }
        });

        // Dialog referansını sakla
        this._acikDialog = dialog;

        // Kilidi kaldır
        this._dialogAciliyor = false;

        // Dialog kapandığında referansı temizle
        if (dialog?.onDialogClose) {
            dialog.onDialogClose.subscribe(() => {
                this._acikDialog = undefined;
            });
        }
    }

    /**
     * Çift tıklandığında düzenle dialogunu aç
     */
    ciftTiklandi(): void {
        this.duzenle();
    }

    /**
     * Aktiviteyi sil
     */
    sil(): void {
        while (this.children.length) {
            this.remove(this.children[0]);
        }

        this.parent?.remove(this);

        if (this.sahne?.anaGosterim) {
            this.sahne.anaGosterim.aktifItem = null;
            this.sahne.anaGosterim.FikoYT.degisti.emit();
            this.sahne.anaGosterim.ciz();
        }
    }

    override ekle?(...objeler: FikoObje3D[]): any {
        super.add(...objeler);
        return this;
    }

    /**
     * JSON'a çevir - Tüm CAM parametreleri dahil
     */
    override toJSON(meta?: any): any {
        return {
            type: this.type,
            isim: this.name,
            uuid: this.uuid,

            // Profil
            profilNoktalar: this.profilNoktalar.map(n => ({ x: n.x, y: n.y, z: n.z })),
            profilKapali: this.profilKapali,

            // Düzlem
            ustZ: this.ustZ,
            altZ: this.altZ,
            guvenliZ: this.guvenliZ,
            araGecisZ: this.araGecisZ,

            // Takım
            takimCap: this.takim.cap,
            takimBoy: this.takim.boy,
            takimKesmeBoy: this.takim.kesmeBoy,
            takimRadius: this.takim.radius,

            // İşleme
            islemeYontemi: this.islemeYontemi,
            yanalYontem: this.yanalYontem,
            koseDonusler: this.koseDonusler,
            zHatve: this.zHatve,
            yanalHatve: this.yanalHatve,
            yanalTekrar: this.yanalTekrar,

            // Giriş parametreleri
            girisYontemi: this.girisYontemi,
            girisUzunluk: this.girisUzunluk,
            girisRampaAcisi: this.girisRampaAcisi,
            girisHeliselCap: this.girisHeliselCap,
            girisHeliselAdim: this.girisHeliselAdim,
            girisYayRadius: this.girisYayRadius,
            girisYayAci: this.girisYayAci,
            girisYonuCW: this.girisYonuCW,
            girisTegetUzunluk: this.girisTegetUzunluk,

            // Çıkış parametreleri
            cikisYontemi: this.cikisYontemi,
            cikisUzunluk: this.cikisUzunluk,
            cikisRampaAcisi: this.cikisRampaAcisi,
            cikisYayRadius: this.cikisYayRadius,
            cikisYayAci: this.cikisYayAci,
            cikisYonuCW: this.cikisYonuCW,

            // Hız parametreleri
            ilerlemeYaklasma: this.ilerlemeYaklasma,
            ilerlemeIsleme: this.ilerlemeIsleme,
            ilerlemeUzaklasma: this.ilerlemeUzaklasma,
            ilerlemeDalma: this.ilerlemeDalma,
            ilerlemeCekme: this.ilerlemeCekme,
            ilerlemeLead: this.ilerlemeLead,
            hizRapid: this.hizRapid,
            devir: this.devir,

            // Ofset
            ofsetYonu: this.ofsetYonu,
            ofsetMiktari: this.ofsetMiktari,
            yuzeyPayi: this.yuzeyPayi,

            // Uzatma & Overlap
            basUzatma: this.basUzatma,
            sonUzatma: this.sonUzatma,
            overlap: this.overlap,
            overcutEtkin: this.overcutEtkin,

            // Son tur
            sonTurEtkin: this.sonTurEtkin,
            sonTurPayi: this.sonTurPayi,
            sonTurIlerleme: this.sonTurIlerleme,
            sonTurDevir: this.sonTurDevir,
            sonTurAyriGiris: this.sonTurAyriGiris,

            // Geçiş
            gecisTipi: this.gecisTipi,
            daireselSapma: this.daireselSapma,

            // Tabs (Köprü)
            tabEtkin: this.tabEtkin,
            tabSekli: this.tabSekli,
            tabGenislik: this.tabGenislik,
            tabYukseklik: this.tabYukseklik,
            tabSayisi: this.tabSayisi,
            tabMesafe: this.tabMesafe,
            tabDagitim: this.tabDagitim,

            // Köşe Yavaşlama
            koseYavaslamaEtkin: this.koseYavaslamaEtkin,
            koseYavaslamaOrani: this.koseYavaslamaOrani,
            koseMinAci: this.koseMinAci,
            koseYavaslamaTipi: this.koseYavaslamaTipi,

            // Kompanzasyon
            kompanzasyonTipi: this.kompanzasyonTipi,
            kompanzasyonTolereans: this.kompanzasyonTolereans,

            // Stay-Down / Lift
            stayDownEtkin: this.stayDownEtkin,
            stayDownMesafe: this.stayDownMesafe,
            kaldirmaMesafesi: this.kaldirmaMesafesi,

            // Yol Yumuşatma
            yumusatmaEtkin: this.yumusatmaEtkin,
            yumusatmaRadius: this.yumusatmaRadius,
            yumusatmaTolerans: this.yumusatmaTolerans,

            // Freze Yönü
            frezeYonu: this.frezeYonu,

            // Çoklu Finiş
            finishGecisSayisi: this.finishGecisSayisi,
            finishStockAzaltma: this.finishStockAzaltma,

            // Güvenlik
            minGuvenliMesafe: this.minGuvenliMesafe,
            carpismaDenetimi: this.carpismaDenetimi
        };
    }

    /**
     * JSON'dan oluştur - Tüm CAM parametreleri dahil
     */
    static fromJson(sahne: FikoSahne, parent: any, json: any): FikoAktiviteProfil {
        const aktivite = new FikoAktiviteProfil(sahne, json.isim);
        aktivite.uuid = json.uuid;
        aktivite.parent = parent;

        // Profil
        if (json.profilNoktalar) {
            aktivite.profilNoktalar = json.profilNoktalar.map(
                (n: any) => new FikoVektor3D(n.x, n.y, n.z)
            );
        }
        if (json.profilKapali !== undefined) aktivite.profilKapali = json.profilKapali;

        // Düzlem
        aktivite.ustZ = json.ustZ ?? 0;
        aktivite.altZ = json.altZ ?? -10;
        aktivite.guvenliZ = json.guvenliZ ?? 5;
        aktivite.araGecisZ = json.araGecisZ ?? 2;

        // Takım
        if (json.takimCap) {
            aktivite.takim = new FikoTakimFrezeleme(sahne, json.takimCap);
            aktivite.takim.boy = json.takimBoy ?? 50;
            aktivite.takim.kesmeBoy = json.takimKesmeBoy ?? 30;
            aktivite.takim.radius = json.takimRadius ?? 0;
        }

        // İşleme
        aktivite.islemeYontemi = json.islemeYontemi ?? 'Tek Yön';
        aktivite.yanalYontem = json.yanalYontem ?? 'İlk Yanal';
        aktivite.koseDonusler = json.koseDonusler ?? 'Keskin';
        aktivite.zHatve = json.zHatve ?? 1;
        aktivite.yanalHatve = json.yanalHatve ?? 0.5;
        aktivite.yanalTekrar = json.yanalTekrar ?? 1;

        // Giriş parametreleri
        aktivite.girisYontemi = json.girisYontemi ?? 'Dikey';
        aktivite.girisUzunluk = json.girisUzunluk ?? 5;
        aktivite.girisRampaAcisi = json.girisRampaAcisi ?? 15;
        aktivite.girisHeliselCap = json.girisHeliselCap ?? 10;
        aktivite.girisHeliselAdim = json.girisHeliselAdim ?? 2;
        aktivite.girisYayRadius = json.girisYayRadius ?? 5;
        aktivite.girisYayAci = json.girisYayAci ?? 90;
        aktivite.girisYonuCW = json.girisYonuCW ?? true;
        aktivite.girisTegetUzunluk = json.girisTegetUzunluk ?? 5;

        // Çıkış parametreleri
        aktivite.cikisYontemi = json.cikisYontemi ?? 'Dikey';
        aktivite.cikisUzunluk = json.cikisUzunluk ?? 5;
        aktivite.cikisRampaAcisi = json.cikisRampaAcisi ?? 15;
        aktivite.cikisYayRadius = json.cikisYayRadius ?? 5;
        aktivite.cikisYayAci = json.cikisYayAci ?? 90;
        aktivite.cikisYonuCW = json.cikisYonuCW ?? false;

        // Hız parametreleri
        aktivite.ilerlemeYaklasma = json.ilerlemeYaklasma ?? 3000;
        aktivite.ilerlemeIsleme = json.ilerlemeIsleme ?? 1500;
        aktivite.ilerlemeUzaklasma = json.ilerlemeUzaklasma ?? 5000;
        aktivite.ilerlemeDalma = json.ilerlemeDalma ?? 500;
        aktivite.ilerlemeCekme = json.ilerlemeCekme ?? 3000;
        aktivite.ilerlemeLead = json.ilerlemeLead ?? 100;
        aktivite.hizRapid = json.hizRapid ?? 10000;
        aktivite.devir = json.devir ?? 3000;

        // Ofset
        aktivite.ofsetYonu = json.ofsetYonu ?? 'Sol';
        aktivite.ofsetMiktari = json.ofsetMiktari ?? 0;
        aktivite.yuzeyPayi = json.yuzeyPayi ?? 0.5;

        // Uzatma & Overlap
        aktivite.basUzatma = json.basUzatma ?? 0;
        aktivite.sonUzatma = json.sonUzatma ?? 0;
        aktivite.overlap = json.overlap ?? 0;
        aktivite.overcutEtkin = json.overcutEtkin ?? false;

        // Son tur
        aktivite.sonTurEtkin = json.sonTurEtkin ?? true;
        aktivite.sonTurPayi = json.sonTurPayi ?? 0.2;
        aktivite.sonTurIlerleme = json.sonTurIlerleme ?? 800;
        aktivite.sonTurDevir = json.sonTurDevir ?? 4000;
        aktivite.sonTurAyriGiris = json.sonTurAyriGiris ?? false;

        // Geçiş
        aktivite.gecisTipi = json.gecisTipi ?? 'Direkt';
        aktivite.daireselSapma = json.daireselSapma ?? 0.01;

        // === YENİ PROFESYONEL CAM PARAMETRELERİ ===

        // Tabs (Köprü/Tutma)
        aktivite.tabEtkin = json.tabEtkin ?? false;
        aktivite.tabSekli = json.tabSekli ?? 'Dikdortgen';
        aktivite.tabGenislik = json.tabGenislik ?? 5;
        aktivite.tabYukseklik = json.tabYukseklik ?? 2;
        aktivite.tabSayisi = json.tabSayisi ?? 4;
        aktivite.tabMesafe = json.tabMesafe ?? 50;
        aktivite.tabDagitim = json.tabDagitim ?? 'Sayi';

        // Köşe Yavaşlama (Corner Slowdown)
        aktivite.koseYavaslamaEtkin = json.koseYavaslamaEtkin ?? false;
        aktivite.koseYavaslamaOrani = json.koseYavaslamaOrani ?? 50;
        aktivite.koseMinAci = json.koseMinAci ?? 90;
        aktivite.koseYavaslamaTipi = json.koseYavaslamaTipi ?? 'Tum';

        // Kompanzasyon
        aktivite.kompanzasyonTipi = json.kompanzasyonTipi ?? 'Bilgisayar';
        aktivite.kompanzasyonTolereans = json.kompanzasyonTolereans ?? 0.05;

        // Stay-Down / Lift
        aktivite.stayDownEtkin = json.stayDownEtkin ?? true;
        aktivite.stayDownMesafe = json.stayDownMesafe ?? 10;
        aktivite.kaldirmaMesafesi = json.kaldirmaMesafesi ?? 2;

        // Yol Yumuşatma (Path Smoothing)
        aktivite.yumusatmaEtkin = json.yumusatmaEtkin ?? false;
        aktivite.yumusatmaRadius = json.yumusatmaRadius ?? 0.5;
        aktivite.yumusatmaTolerans = json.yumusatmaTolerans ?? 0.01;

        // Freze Yönü (Climb vs Conventional)
        aktivite.frezeYonu = json.frezeYonu ?? 'Tirmanma';

        // Çoklu Finiş Geçişi
        aktivite.finishGecisSayisi = json.finishGecisSayisi ?? 1;
        aktivite.finishStockAzaltma = json.finishStockAzaltma ?? 0.1;

        // Güvenlik
        aktivite.minGuvenliMesafe = json.minGuvenliMesafe ?? 1;
        aktivite.carpismaDenetimi = json.carpismaDenetimi ?? true;

        return aktivite;
    }
}
